/**
 * iFrame Host Script
 *
 * Lightweight script that runs on the WordPress page.
 * Responsibilities:
 * - Paints a static launcher placeholder, and creates the iframe only once the
 *   visitor reaches for it (hover, focus, press) or theme code calls the API --
 *   the iframe is a second WordPress bootstrap, not worth paying on every view
 * - Creates and positions the iframe
 * - Manages iframe resize (toggle button vs drawer open)
 * - Creates and manages the overlay on the host page
 * - Handles fixed-element adjustments for sidebar mode
 * - Listens for postMessage events from the iframe
 */
import {
	DEFAULT_BUBBLE_SIZE,
	getBubbleButtonPx,
	getBubbleSizeCss,
	getIframeSizePx,
	getStickyBarPx,
} from './scripts/lib/bubble-sizes';
import {
	PROMPT_SETTLE_MS,
	TEASER_STORAGE_KEY,
	clampInt,
	normalizePrompt,
	normalizeSource,
	normalizeTopic,
	normalizeVariant,
	parseHashTrigger,
	parseOpenOptions,
	resolveEntryVariant,
	shouldShowTeaser,
	type EntryVariantOrUnset,
} from './scripts/lib/entry-points';
import {
	MORPH_LAND_TIMEOUT_MS,
	beginColdStartPress,
	beginLandingSession,
	beginMorphOriginSession,
	isMorphTrigger,
	measureMorphOrigin,
	readEntrySource,
	readMotionEnvironment,
	readPlacement,
	resolveMorphMotionFlags,
	setupTypingEntries,
	type ColdStartPress,
	type MorphLandingSession,
} from './scripts/lib/entry-surfaces';
import type {
	EntrySource,
	HostInlineOpen,
	HostMorphOpen,
	HostOpenOrigin,
	IframeCapability,
	IframeToHostMessage,
	LauncherVisualState,
} from './scripts/lib/iframe-protocol';
import {
	MESSAGE_PREFIX,
	isIframeToHostMessage,
	isSurfaceRectMessage,
	isTrustedOrigin,
	sanitizeCapabilities,
	sendToIframe,
} from './scripts/lib/iframe-protocol';
import {
	InlineChatSlot,
	clearInlineIframeStyles,
	isInlineExpandForm,
} from './scripts/lib/inline-chat';
import {
	ensureViewportFitCover,
	enterMobileFullscreen,
	exitMobileFullscreen,
	getEffectiveSafeAreaInsets,
	getKeyboardInsetPx,
	isMobileFullscreenViewport,
	syncMobileFullscreen,
} from './scripts/lib/mobile-fullscreen-host';
import { isMobileShell } from './scripts/lib/mobile-shell';
import {
	MOTION_CLOSE_MS,
	MOTION_OPEN_MS,
	MOTION_SAFETY_MS,
	getRectCenter,
} from './scripts/lib/motion';
import {
	SECTION_ROOT_MARGIN,
	SECTION_SELECTOR,
	pickActiveSection,
	readSection,
	sameSection,
	swapDelay,
	type ProactiveSection,
} from './scripts/lib/proactive-sections';
import {
	QUICK_SELECTOR,
	findQuickQuestion,
} from './scripts/lib/quick-questions';
import {
	barTeaserGeometry,
	resolveTeaserAnchor,
	type TeaserAnchor,
} from './scripts/lib/teaser-anchor';
import {
	createScrollGate,
	type ScrollGate,
} from './scripts/lib/teaser-scroll-gate';
import { createTracker, type Tracker } from './scripts/lib/tracking';
// @ts-expect-error - Image is not a valid module
import TOGGLE_ICON from './scripts/components/StickyChat/assets/toggle-icon.svg';
import './styles/iframe-host.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const IFRAME_ID = 'dhsv100-ai-chat-iframe';
const HOST_CONTAINER_ID = 'dhsv100-chat-host';
const PLACEHOLDER_CLASS = 'dhsv100-chat-placeholder';
const OVERLAY_CLASS = 'dhsv100-chat-overlay';

// Every variant that paints a backdrop, so show and hide agree on what to clear.
const OVERLAY_VARIANTS = [
	'default',
	'sidebar',
	'fullscreen',
	'island',
] as const;

// Longest overlay fade across the variants (open or close token, plus a safety
// margin). The variant class carries the backdrop, so it has to outlive the
// fade -- see `hideOverlay()`.
const OVERLAY_FADE_MS =
	Math.max( MOTION_OPEN_MS, MOTION_CLOSE_MS ) + MOTION_SAFETY_MS;
const SIDEBAR_OPEN_CLASS = 'dhsv100-chat-sidebar-open';

// DOM event dispatched on window once window.DhsvChat is installed, so themes
// have a deterministic readiness signal instead of polling for the global.
const DHSV_CHAT_READY_EVENT = 'dhsv100-chat-api-ready';

// Duration of the vaul drawer close animation (ms) -- wait this long before
// shrinking the iframe after the drawer closes. Derived from the close token,
// so it is never shorter than the CSS it trails.
const DRAWER_ANIMATION_MS = MOTION_CLOSE_MS + MOTION_SAFETY_MS;

// How long a command may wait for the iframe app to report 'chat-ready'
// before it is discarded. Measured from the most recent deferral.
const CHAT_READY_TIMEOUT_MS = 10_000;

// Prevents flicker when crossing scroll thresholds.
const LAUNCHER_HYSTERESIS_PX = 24;

// Fallback surface colour when the host container carries no configured chat
// background -- the same default `Options::get_default_style_config()` uses.
const DEFAULT_CHAT_BACKGROUND = '#ffffff';

// Viewport width from which the sidebar shifts the page instead of covering
// it (`iframe-host.scss`). Below it the sidebar is modal like the other variants.
const PAGE_SHIFT_QUERY = '(min-width: 1500px)';

// Siblings of the chat container that carry no content to hide from the visitor.
const INERT_SKIP_TAGS = new Set( [
	'SCRIPT',
	'STYLE',
	'LINK',
	'NOSCRIPT',
	'TEMPLATE',
] );

// A theme marks a banner or dialog that must stay operable while the chat is open.
const INERT_EXCLUDE_ATTRIBUTE = 'data-dhsv-chat-inert-exclude';

// Used for the iframe title when the host container carries none.
const DEFAULT_HEADER_TITLE = 'KI-Assistent';

// Backdrop fallbacks, matching `Options.php`. A cached host page that predates
// the `data-backdrop-*` attributes must still get a sane overlay.
const DEFAULT_BACKDROP_COLOR = '#000000';
const DEFAULT_BACKDROP_OPACITY = 15;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type DrawerVariant = 'default' | 'sidebar' | 'fullscreen' | 'island';
/** `inline`: the chat runs in the page flow, over a form's slot (inline-chat.ts). */
type DrawerState = 'closed' | DrawerVariant | 'inline';
type ChatStatus = 'idle' | 'streaming' | 'submitted' | 'error';
type ChatMessage = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	timestamp: number;
};
type OpenWithMessageOptions = {
	variant?: DrawerVariant;
	startNewChat?: boolean;
};
/** `DhsvChat.open( { ... } )`: every field is optional. */
/**
 * Whether the iframe announced the `morph` capability. The host only adds a
 * `morph` field to an open command when this is true.
 */
export function iframeSupportsMorph(): boolean {
	return iframeCapabilities.includes( 'morph' );
}

export type DhsvChatOpenOptions = {
	/** A question sent as the visitor's own message (queued behind a running answer). */
	prompt?: string;
	/** Tracking metadata for the `open` event only; never sent to the chat service. */
	topic?: string;
	variant?: DrawerVariant;
	/** Where the open came from, `^[a-z][a-z0-9_-]{0,31}$`; defaults to `api`. */
	source?: string;
};
/** What every `open` event listener receives. */
export type DhsvChatOpenPayload = {
	source: string;
	/** Additive: `inline` when the chat opened in the page flow. */
	variant: DrawerVariant | 'inline';
	topic?: string;
	hasPrompt: boolean;
	/** `true` only on the emission that leaves the closed state. */
	sessionStart: boolean;
	/** Additive: the trigger's `data-dhsv-chat-entry`, when it has one. */
	entry?: EntrySource;
	/** Additive: `true` when the chat morphs out of its trigger. */
	morph?: boolean;
};
type OpenMeta = {
	source: string;
	topic?: string;
	hasPrompt: boolean;
	entry?: EntrySource;
	morph?: boolean;
};
/** A normalised open command, before the readiness gate. */
type OpenRequest = {
	variant: DrawerVariant;
	/** The caller asked for this variant; an open chat follows it. */
	explicitVariant?: boolean;
	/**
	 * The host already resolved the variant (surface rule or explicit
	 * variant); the iframe opens exactly that, even from a sticky bar.
	 */
	hostResolved?: boolean;
	prompt: string;
	topic?: string;
	source: string;
	/** Prefill the composer without sending (a key typed on a morph trigger). */
	draft?: string;
	/** A `data-dhsv-chat-expand="inline"` form: open in the page flow. */
	inlineForm?: HTMLElement;
};
/** What a trigger wants to know about its pending command. */
type CommandHooks = {
	/** Carries `aria-busy="true"` while the command waits for `chat-ready`. */
	busyElement?: Element | null;
	/** The command ran (immediately, or after `chat-ready`). */
	onDelivered?: () => void;
	/** Another command took the single pending slot. */
	onReplaced?: () => void;
	/** `CHAT_READY_TIMEOUT_MS` passed without `chat-ready`. */
	onDiscarded?: () => void;
};
type DhsvChatEvent =
	| 'ready'
	| 'open'
	| 'close'
	| 'message'
	| 'messages-changed'
	| 'status-changed'
	| 'response-done';

interface DhsvChatApi {
	open: ( options?: DrawerVariant | DhsvChatOpenOptions ) => void;
	close: () => void;
	toggle: ( variant?: DrawerVariant ) => void;
	isOpen: () => boolean;
	openWithMessage: (
		text: string,
		options?: DrawerVariant | OpenWithMessageOptions
	) => void;
	openWithNewMessage: ( text: string, variant?: DrawerVariant ) => void;
	sendMessage: ( text: string ) => void;
	newChat: () => void;
	clearHistory: () => void;
	openHistory: () => void;
	getMessages: () => ChatMessage[];
	getStatus: () => ChatStatus;
	on: (
		event: DhsvChatEvent,
		callback: ( payload?: unknown ) => void
	) => void;
	off: (
		event: DhsvChatEvent,
		callback: ( payload?: unknown ) => void
	) => void;
	preload: () => void;
	help: () => void;
}

export type { DhsvChatApi };
export type DhsvChatReadyEvent = CustomEvent< { api: DhsvChatApi } >;

declare global {
	interface Window {
		// Optional: absent until init() installs it, and never installed at all
		// on pages without a host container. The truthiness guard themes are
		// told to use only narrows if the type admits undefined.
		DhsvChat?: DhsvChatApi;
	}
	interface WindowEventMap {
		[ DHSV_CHAT_READY_EVENT ]: DhsvChatReadyEvent;
	}
}

let currentState: DrawerState = 'closed';
let chatBackgroundColor = DEFAULT_CHAT_BACKGROUND;
let backdropRgb = '0 0 0';
let backdropAlpha = DEFAULT_BACKDROP_OPACITY / 100;
// Set by a host-side trigger (placeholder) for the open call that follows.
let openTrigger: Element | null = null;
// Where the placeholder's controls were when it was handed over. The handoff
// removes the placeholder before a deferred open runs, so this is the only
// position left to grow the chat from.
const handoffRects: WeakMap< Element, DOMRect > = new WeakMap();
// Host focus management: where the visitor came from, and what the host made
// inert while a modal variant is open (only what it set itself is undone).
let focusReturnTarget: Element | null = null;
// An open session whose focus has not been restored yet (a reopen during the
// close delay keeps the first target).
let restoreFocusPending = false;
type InertRecord = { mode: 'inert' | 'aria-hidden'; ariaHidden: string | null };
const inertRecords: Map< Element, InertRecord > = new Map();
let inertObserver: MutationObserver | null = null;
let inertFocusGuard: ( ( event: FocusEvent ) => void ) | null = null;
let escListenerAttached = false;
let pageShiftQuery: MediaQueryList | null = null;
let mobileResizeListenerAttached = false;
let iframe: HTMLIFrameElement | null = null;
let hostElement: HTMLElement | null = null;
let placeholder: HTMLDivElement | null = null;
let placeholderInput: HTMLInputElement | null = null;
let overlay: HTMLDivElement | null = null;
let overlayFadeTimer: ReturnType< typeof setTimeout > | null = null;
let closeTimer: ReturnType< typeof setTimeout > | null = null;
let visualViewportTracking = false;
let isBubbleVisible = true;
let launcherMode: 'bubble' | 'sticky_bar' = 'bubble';
let launcherCollapseAfterPx = 400;
let launcherRestoreNearBottomPx = 240;
let currentLauncherState: LauncherVisualState = 'bar';
let stickyBarContentHeightPx = 0;
let scrollListenerAttached = false;
let launcherViewportListenerAttached = false;
let wasStickyBarViewport = false;
let scrollRafId: number | null = null;
let messagesCache: ChatMessage[] = [];
let statusCache: ChatStatus = 'idle';
let previousMessageCount = 0;

// Morph entries (`entry-surfaces.ts`): the origin of the running morph open,
// from the open until the iframe shrank after its landing.
// A launcher morph (started inside the iframe) only has the landing half.
let morphSession: MorphLandingSession | null = null;
// The inline slot the chat runs in (or collapses out of); null otherwise.
let inlineSlot: InlineChatSlot | null = null;
// The collapse landed and the host waits for the iframe's `inline-ended`.
let inlineEndTimer: ReturnType< typeof setTimeout > | null = null;
const INLINE_END_TIMEOUT_MS = 600;
// Cold start: a morph trigger pressed before 'chat-ready', held until its
// open runs (`prepareMorphOpen()` takes it over) or is dropped.
let coldStartPress: { press: ColdStartPress; owner: object } | null = null;
// Safety net for the shrink after a morph close, stretched by `?slow`.
let morphLandTimeoutMs = MORPH_LAND_TIMEOUT_MS;

// Readiness gate: the iframe app mounts well after the host script runs, and
// only then listens for host commands. Until it reports 'chat-ready', a
// command is held here rather than applied -- expanding the iframe around a
// drawer that cannot open would strand the page.
let isChatReady = false;
// Optional features the iframe announced on 'chat-ready'. Empty for an older
// iframe, so the host never sends fields it would not understand.
let iframeCapabilities: IframeCapability[] = [];
let pendingCommand:
	| ( { label: string; run: () => void } & CommandHooks )
	| null = null;
let pendingCommandTimer: ReturnType< typeof setTimeout > | null = null;
const listeners: Map<
	DhsvChatEvent,
	Set< ( payload?: unknown ) => void >
> = new Map();

// Entry points ---------------------------------------------------------------

// The container exists only because a block asked for it on a page the chat is
// not configured for (or the bubble is switched off): no launcher, no teaser.
let hostForced = false;
// Where the open session that is running came from; repeated with
// `sessionStart: false` on echoes and variant changes, cleared on close.
let sessionMeta: OpenMeta | null = null;

// The prompt that was last sent into the chat by an entry point, for the
// double-click rule and the "a different prompt right behind it" rule.
let lastDelivered: { text: string; at: number } | null = null;
// One slot, the latest prompt wins. Sent when the running answer finished,
// dropped on close, error, Stop and the visitor's own message.
let queuedPrompt: string | null = null;
// Messages the host itself caused (open-with-message, send-message) that the
// iframe has not reported yet, so they are not mistaken for the visitor's own.
let unseenHostSends = 0;
let lastUserMessageCount = 0;

// Data layer tracking (`tracking.ts`): created by init(), a page-level
// singleton. Null until then; every call goes through `track()` so a failure
// in tracking can never stop the chat action that caused the event.
let tracker: Tracker | null = null;

function track( run: ( activeTracker: Tracker ) => void ): void {
	if ( ! tracker ) {
		return;
	}
	try {
		run( tracker );
	} catch {
		// Tracking must never break the chat.
	}
}

function emit( event: DhsvChatEvent, payload?: unknown ): void {
	const handlers = listeners.get( event );
	if ( ! handlers ) {
		return;
	}
	handlers.forEach( ( handler ) => {
		handler( payload );
	} );
}

// ---------------------------------------------------------------------------
// Fixed Elements (moved from StickyChat/lib/utils.ts)
// ---------------------------------------------------------------------------

function getFixedElements(): Element[] {
	const allElements = document.querySelectorAll( '*' );
	return Array.from( allElements ).filter( ( element ) => {
		const computedStyle = window.getComputedStyle( element );
		return computedStyle.getPropertyValue( 'position' ) === 'fixed';
	} );
}

function tagFixedElements(): void {
	const fixedElements = getFixedElements();
	fixedElements.forEach( ( element ) => {
		// Don't tag our own elements
		if (
			element.id === IFRAME_ID ||
			element.classList.contains( OVERLAY_CLASS )
		) {
			return;
		}
		element.classList.add( 'fixed-element' );
	} );
}

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

function createOverlay(): HTMLDivElement {
	const el = document.createElement( 'div' );
	el.className = OVERLAY_CLASS;
	// A decorative click target: Escape and the close controls are the
	// accessible ways out.
	el.setAttribute( 'aria-hidden', 'true' );
	el.setAttribute( 'role', 'presentation' );
	applyBackdropConfig( el );
	el.addEventListener( 'click', () => {
		// Send close request to iframe
		if ( iframe ) {
			sendToIframe( iframe, {
				type: MESSAGE_PREFIX,
				action: 'close-request',
			} );
		}
	} );
	document.body.appendChild( el );
	return el;
}

function overlayVariantClasses(): string[] {
	return OVERLAY_VARIANTS.map(
		( variant ) => `${ OVERLAY_CLASS }--${ variant }`
	);
}

/**
 * `#rrggbb` (or `#rgb`) to the space-separated channels `rgb( r g b / a )`
 * takes. Anything else, including a missing attribute, becomes black.
 * @param value
 */
function parseBackdropRgb( value: string | null ): string {
	const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(
		( value || '' ).trim()
	);
	if ( ! match ) {
		return '0 0 0';
	}
	let hex = match[ 1 ] as string;
	if ( hex.length === 3 ) {
		hex = hex
			.split( '' )
			.map( ( char ) => char + char )
			.join( '' );
	}
	return [ 0, 2, 4 ]
		.map( ( at ) => parseInt( hex.slice( at, at + 2 ), 16 ) )
		.join( ' ' );
}

/**
 * Backdrop opacity in percent: NaN falls back to the default, anything out of
 * range is clamped into 0-100.
 * @param value
 */
function parseBackdropOpacity( value: string | null ): number {
	const parsed =
		value === null || value.trim() === '' ? NaN : Number( value );
	if ( ! Number.isFinite( parsed ) ) {
		return DEFAULT_BACKDROP_OPACITY;
	}
	return Math.min( 100, Math.max( 0, Math.round( parsed ) ) );
}

function readBackdropConfig( hostContainer: HTMLElement ): void {
	backdropRgb = parseBackdropRgb(
		hostContainer.getAttribute( 'data-backdrop-color' )
	);
	backdropAlpha =
		parseBackdropOpacity(
			hostContainer.getAttribute( 'data-backdrop-opacity' )
		) / 100;
}

function applyBackdropConfig( el: HTMLElement ): void {
	el.style.setProperty( '--dhsv100-backdrop-rgb', backdropRgb );
	el.style.setProperty( '--dhsv100-backdrop-alpha', String( backdropAlpha ) );
	// Nothing to paint: let CSS drop the surface entirely (desktop shell).
	el.classList.toggle(
		`${ OVERLAY_CLASS }--no-backdrop`,
		backdropAlpha === 0
	);
}

function showOverlay( variant: DrawerVariant ): void {
	if ( ! overlay ) {
		overlay = createOverlay();
	}
	if ( overlayFadeTimer ) {
		clearTimeout( overlayFadeTimer );
		overlayFadeTimer = null;
	}
	// Remove previous variant classes
	overlay.classList.remove( ...overlayVariantClasses() );
	overlay.classList.add(
		`${ OVERLAY_CLASS }--visible`,
		`${ OVERLAY_CLASS }--${ variant }`
	);
}

function hideOverlay(): void {
	if ( ! overlay ) {
		return;
	}

	overlay.classList.remove( `${ OVERLAY_CLASS }--visible` );

	// The variant class carries the backdrop colour. Dropping it
	// in the same frame as `--visible` leaves the opacity transition nothing to
	// fade, so the dim pops off while the drawer is still animating out.
	if ( overlayFadeTimer ) {
		clearTimeout( overlayFadeTimer );
	}
	overlayFadeTimer = setTimeout( () => {
		overlay?.classList.remove( ...overlayVariantClasses() );
		overlayFadeTimer = null;
	}, OVERLAY_FADE_MS );
}

// ---------------------------------------------------------------------------
// iFrame helpers
// ---------------------------------------------------------------------------

function getToggleIframeSize( hostContainer: Element ): {
	width: string;
	height: string;
} {
	if ( ! isBubbleVisible ) {
		return { width: '0px', height: '0px' };
	}
	const key =
		hostContainer.getAttribute( 'data-bubble-size' ) || DEFAULT_BUBBLE_SIZE;
	const { width, height } = getIframeSizePx( key );
	return { width: `${ width }px`, height: `${ height }px` };
}

function isStickyBarMode(): boolean {
	return (
		launcherMode === 'sticky_bar' &&
		isBubbleVisible &&
		// In the mobile shell the sticky bar is out of scope and the iframe app
		// renders the round bubble instead (`useIsMobile()`, same condition).
		// The host has to agree, or it sizes the iframe as a full-width strip
		// while the bubble inside needs the taller corner box, which clips it.
		! isMobileShell()
	);
}

function getStripIframeSize( hostContainer?: Element | null ): {
	width: string;
	height: string;
} {
	if ( ! isBubbleVisible ) {
		return { width: '0px', height: '0px' };
	}

	let heightPx = getIframeSizePx( DEFAULT_BUBBLE_SIZE ).height;
	if ( hostContainer ) {
		const key =
			hostContainer.getAttribute( 'data-bubble-size' ) ||
			DEFAULT_BUBBLE_SIZE;
		heightPx = getIframeSizePx( key ).height;
	}

	return {
		width: '100%',
		height: `calc(${ heightPx }px + env(safe-area-inset-bottom, 0px))`,
	};
}

function applyCollapsedIframeGeometry(): void {
	if ( ! iframe ) {
		return;
	}

	iframe.style.top = '';
	iframe.style.left = '';

	if ( isStickyBarMode() ) {
		const hostContainer = iframe.parentElement;
		const size = getStripIframeSize( hostContainer );
		iframe.style.position = 'fixed';
		iframe.style.inset = 'auto 0 0 0';
		iframe.style.width = size.width;
		iframe.style.height =
			currentLauncherState === 'bar' && stickyBarContentHeightPx > 0
				? `${ stickyBarContentHeightPx }px`
				: size.height;
		// Clicks must reach the iframe document; pass-through is handled inside
		// iframe-content.scss (body pointer-events:none, launcher pointer-events:auto).
		iframe.style.pointerEvents = 'auto';
		return;
	}

	const hostContainer = iframe.parentElement;
	const size = hostContainer
		? getToggleIframeSize( hostContainer )
		: getDefaultIframeSizePx();
	iframe.style.position = 'fixed';
	iframe.style.inset = 'auto 0 0 auto';
	iframe.style.width = size.width;
	iframe.style.height = size.height;
	iframe.style.pointerEvents = 'auto';
}

function computeLauncherState( useHysteresis: boolean ): LauncherVisualState {
	if ( ! isStickyBarMode() ) {
		return 'bubble';
	}

	const scrollY = window.scrollY;
	const docHeight = Math.max(
		document.documentElement.scrollHeight,
		document.body.scrollHeight
	);
	const viewportHeight = window.innerHeight;
	const remaining = docHeight - scrollY - viewportHeight;

	if ( docHeight <= viewportHeight + launcherCollapseAfterPx ) {
		return 'bar';
	}

	if ( remaining <= launcherRestoreNearBottomPx ) {
		return 'bar';
	}

	if ( scrollY < launcherCollapseAfterPx ) {
		return 'bar';
	}

	if (
		useHysteresis &&
		currentLauncherState === 'bar' &&
		scrollY < launcherCollapseAfterPx + LAUNCHER_HYSTERESIS_PX
	) {
		return 'bar';
	}

	if (
		useHysteresis &&
		currentLauncherState === 'bubble' &&
		remaining <= launcherRestoreNearBottomPx + LAUNCHER_HYSTERESIS_PX
	) {
		return 'bar';
	}

	return 'bubble';
}

function pushLauncherState( force = false ): void {
	if ( currentState !== 'closed' || ! isStickyBarMode() ) {
		return;
	}

	const state = computeLauncherState( ! force );
	if ( ! iframe ) {
		currentLauncherState = state;
		updatePlaceholderState();
		positionTeaser();
		return;
	}
	if ( ! force && currentLauncherState === state ) {
		return;
	}
	currentLauncherState = state;
	updatePlaceholderState();
	positionTeaser();
	sendToIframe( iframe, {
		type: MESSAGE_PREFIX,
		action: 'set-launcher-state',
		state,
	} );
}

function updateLauncherStateFromScroll(): void {
	pushLauncherState( false );
}

function scheduleLauncherStateUpdate(): void {
	if ( scrollRafId !== null ) {
		return;
	}
	scrollRafId = window.requestAnimationFrame( () => {
		scrollRafId = null;
		updateLauncherStateFromScroll();
	} );
}

function setupScrollObserver(): void {
	if ( scrollListenerAttached || ! isStickyBarMode() ) {
		return;
	}

	const onScrollOrResize = () => {
		scheduleLauncherStateUpdate();
	};

	window.addEventListener( 'scroll', onScrollOrResize, { passive: true } );
	window.addEventListener( 'resize', onScrollOrResize, { passive: true } );
	window.addEventListener( 'load', () => {
		pushLauncherState( true );
	} );
	window.addEventListener( 'pageshow', () => {
		pushLauncherState( true );
	} );
	scrollListenerAttached = true;

	currentLauncherState = computeLauncherState( false );
	if ( isChatReady ) {
		pushLauncherState( true );
	}
}

/**
 * Re-apply the collapsed geometry when the viewport crosses the sticky-bar
 * breakpoint.
 *
 * The scroll observer only attaches while the sticky bar is actually in play,
 * so on a phone-sized viewport nothing would notice a rotation into tablet
 * width -- the iframe would keep the bubble box while the app switched to the
 * bar (or the other way round, clipping the bubble).
 */
function setupLauncherViewportObserver(): void {
	if ( launcherViewportListenerAttached || launcherMode !== 'sticky_bar' ) {
		return;
	}

	wasStickyBarViewport = isStickyBarMode();

	const onResize = () => {
		const isSticky = isStickyBarMode();
		if ( isSticky === wasStickyBarViewport ) {
			return;
		}
		wasStickyBarViewport = isSticky;

		if ( isSticky ) {
			setupScrollObserver();
		}
		if ( currentState === 'closed' && ! closeTimer ) {
			applyCollapsedIframeGeometry();
		}
		updatePlaceholderState();
		if ( isSticky ) {
			pushLauncherState( true );
		}
		positionTeaser();
	};

	window.addEventListener( 'resize', onResize, { passive: true } );
	launcherViewportListenerAttached = true;
}

function readLauncherConfig( hostContainer: HTMLElement ): void {
	const mode = hostContainer.getAttribute( 'data-launcher-mode' );
	launcherMode = mode === 'sticky_bar' ? 'sticky_bar' : 'bubble';

	const collapseRaw = hostContainer.getAttribute( 'data-collapse-after-px' );
	const restoreRaw = hostContainer.getAttribute(
		'data-restore-near-bottom-px'
	);
	launcherCollapseAfterPx = collapseRaw
		? Math.max( 0, parseInt( collapseRaw, 10 ) || 400 )
		: 400;
	launcherRestoreNearBottomPx = restoreRaw
		? Math.max( 0, parseInt( restoreRaw, 10 ) || 240 )
		: 240;
}

function getDefaultIframeSizePx(): { width: string; height: string } {
	if ( ! isBubbleVisible ) {
		return { width: '0px', height: '0px' };
	}
	const { width, height } = getIframeSizePx( DEFAULT_BUBBLE_SIZE );
	return { width: `${ width }px`, height: `${ height }px` };
}

/**
 * Should the expanded iframe track `window.visualViewport`?
 *
 * In the mobile shell (tablets keep the layout viewport size and report the
 * keyboard overlap as `keyboardInset` instead, see `pushSafeAreaInsets()`) the
 * on-screen keyboard shrinks the visual viewport and the browser
 * scrolls the layout viewport underneath it. A `100svh` iframe then sticks out
 * past the visible area and the host page shows through next to the chat, so on
 * narrow screens the iframe follows the visual viewport instead.
 */
function shouldTrackVisualViewport(): boolean {
	return !! window.visualViewport && isMobileShell();
}

function applyExpandedIframeGeometry(): void {
	if ( ! iframe ) {
		return;
	}

	clearInlineIframeStyles( iframe );
	iframe.style.position = 'fixed';
	iframe.style.pointerEvents = 'auto';

	const viewport = window.visualViewport;
	if ( viewport && shouldTrackVisualViewport() ) {
		iframe.style.inset = 'auto';
		iframe.style.top = `${ Math.round( viewport.offsetTop ) }px`;
		iframe.style.left = `${ Math.round( viewport.offsetLeft ) }px`;
		iframe.style.width = `${ Math.round( viewport.width ) }px`;
		iframe.style.height = `${ Math.round( viewport.height ) }px`;
		return;
	}

	iframe.style.inset = '0';
	iframe.style.top = '';
	iframe.style.left = '';
	iframe.style.width = '100vw';
	iframe.style.height = '100svh';
}

function handleVisualViewportChange(): void {
	// The inline slot follows the page itself.
	if ( currentState === 'closed' || currentState === 'inline' ) {
		return;
	}
	applyExpandedIframeGeometry();
	// The keyboard swallows the home-indicator inset as it comes up, so the
	// padding the chat applies has to be recomputed on the same tick as the
	// geometry -- otherwise the composer visibly settles a second time.
	pushSafeAreaInsets();
}

function startVisualViewportTracking(): void {
	const viewport = window.visualViewport;
	if ( ! viewport || visualViewportTracking ) {
		return;
	}
	viewport.addEventListener( 'resize', handleVisualViewportChange );
	viewport.addEventListener( 'scroll', handleVisualViewportChange );
	visualViewportTracking = true;
}

function stopVisualViewportTracking(): void {
	const viewport = window.visualViewport;
	if ( ! viewport || ! visualViewportTracking ) {
		return;
	}
	viewport.removeEventListener( 'resize', handleVisualViewportChange );
	viewport.removeEventListener( 'scroll', handleVisualViewportChange );
	visualViewportTracking = false;
}

// ---------------------------------------------------------------------------
// Mobile fullscreen shell
// ---------------------------------------------------------------------------

/**
 * Tell the iframe how much of its edges the notch and the home indicator take.
 *
 * The iframe covers the visual viewport edge to edge -- that is what keeps the
 * background seamless -- but `env(safe-area-inset-*)` reads as zero inside an
 * iframe, so without this the composer would sit under the home indicator.
 */
function pushSafeAreaInsets(): void {
	if ( ! iframe ) {
		return;
	}

	const insets = isMobileFullscreenViewport()
		? getEffectiveSafeAreaInsets()
		: { top: 0, right: 0, bottom: 0, left: 0 };

	sendToIframe( iframe, {
		type: MESSAGE_PREFIX,
		action: 'set-safe-area',
		top: insets.top,
		right: insets.right,
		bottom: insets.bottom,
		left: insets.left,
		keyboardInset: getKeyboardInsetPx(),
	} );
}

/**
 * Keep the phone-only shell in step with orientation changes and window
 * resizes while the chat is open.
 *
 * `visualViewport` alone is not enough: crossing the mobile-shell condition has to
 * add or drop the scroll lock, and that is a layout-viewport event.
 */
function setupMobileFullscreenObserver(): void {
	if ( mobileResizeListenerAttached ) {
		return;
	}

	window.addEventListener(
		'resize',
		() => {
			if ( currentState === 'closed' || currentState === 'inline' ) {
				return;
			}
			syncMobileFullscreen( chatBackgroundColor );
			// The shell condition may have changed: re-apply the geometry
			// (visual-viewport box on phones, full layout viewport otherwise).
			applyExpandedIframeGeometry();
			pushSafeAreaInsets();
		},
		{ passive: true }
	);
	mobileResizeListenerAttached = true;
}

/**
 * Expand the iframe to cover the full viewport.
 * This happens INSTANTLY (no CSS transitions) so the vaul drawer
 * animation is the only visual transition the user sees.
 *
 * After expansion, sends an 'expanded' message to the iframe so it
 * knows the viewport is ready and can safely open the drawer.
 * @param focusIframe
 */
function expandIframe( focusIframe = false ): void {
	if ( ! iframe ) {
		return;
	}
	// Before the geometry: the scroll lock pins the layout viewport, and
	// measuring the visual viewport after it settles avoids a second pass.
	enterMobileFullscreen( chatBackgroundColor );
	applyExpandedIframeGeometry();
	startVisualViewportTracking();
	setupMobileFullscreenObserver();
	pushSafeAreaInsets();

	// Notify the iframe that expansion is complete.
	// Use rAF to ensure the browser has applied the new dimensions.
	requestAnimationFrame( () => {
		if ( iframe ) {
			sendToIframe( iframe, {
				type: MESSAGE_PREFIX,
				action: 'expanded',
			} );
			// Opening a dialog moves focus into it, whatever opened it; the
			// focus trap inside the iframe takes over from here.
			if ( focusIframe ) {
				iframe.focus();
			}
		}
	} );
}

/**
 * Shrink the iframe back to the toggle-button size (bottom-right corner).
 * Called AFTER the vaul drawer close animation completes.
 */
function shrinkIframe(): void {
	stopVisualViewportTracking();
	if ( iframe ) {
		clearInlineIframeStyles( iframe );
	}
	applyCollapsedIframeGeometry();
}

// ---------------------------------------------------------------------------
// Inline chat (in the page flow)
// ---------------------------------------------------------------------------

/**
 * Let go of an inline slot at once: the chat moved to another surface, or a
 * new inline open replaces a collapsing one. The spacer springs back on its
 * own; the iframe is the caller's to place.
 */
function releaseInlineSlot(): void {
	const slot = inlineSlot;
	inlineSlot = null;
	if ( inlineEndTimer ) {
		clearTimeout( inlineEndTimer );
		inlineEndTimer = null;
	}
	if ( iframe ) {
		iframe.style.visibility = '';
	}
	slot?.release();
}

/**
 * The collapse landed: hide the iframe, ask the app to drop its inline
 * surface, and finish the close once it answers (or after a timeout).
 */
function endInlineCollapse(): void {
	if ( iframe ) {
		// Until the app removed the inline surface: the collapsed geometry
		// must not show it in the launcher box.
		iframe.style.visibility = 'hidden';
		sendToIframe( iframe, { type: MESSAGE_PREFIX, action: 'inline-end' } );
	}
	if ( inlineEndTimer ) {
		clearTimeout( inlineEndTimer );
	}
	inlineEndTimer = setTimeout( completeInlineEnd, INLINE_END_TIMEOUT_MS );
}

function completeInlineEnd(): void {
	if ( ! inlineEndTimer ) {
		return;
	}
	clearTimeout( inlineEndTimer );
	inlineEndTimer = null;
	inlineSlot = null;
	// Shrink, release, and focus back to the field (focusReturnTarget).
	finishClose();
	if ( iframe ) {
		iframe.style.visibility = '';
	}
}

/**
 * Bind the iframe to the inline slot and grow it; the app hears `expanded`
 * once the geometry is in place, and focus moves into the chat.
 *
 * @param {boolean} focusIframe Whether this open starts the session.
 */
function attachInlineSlot( focusIframe: boolean ): void {
	const slot = inlineSlot;
	if ( ! iframe || ! slot || slot.isAttached || slot.isClosing ) {
		return;
	}
	stopVisualViewportTracking();
	slot.open( iframe );
	requestAnimationFrame( () => {
		if ( ! iframe || currentState !== 'inline' ) {
			return;
		}
		sendToIframe( iframe, { type: MESSAGE_PREFIX, action: 'expanded' } );
		if ( focusIframe ) {
			iframe.focus( { preventScroll: true } );
		}
	} );
}

// ---------------------------------------------------------------------------
// Host focus management
// ---------------------------------------------------------------------------

/**
 * Where focus should go when the chat closes, recorded when it opens.
 *
 * Only a user-initiated open counts: an explicit trigger that is still in the
 * document (the launcher placeholder, which a handoff has removed by the time a
 * deferred open runs), or the focused element while the browser reports a user
 * activation. A timer-driven `DhsvChat.open()` must not capture whatever input
 * happens to have focus, or closing would teleport the visitor back into it. A
 * browser without `navigator.userActivation` records nothing and degrades to
 * the launcher inside the iframe.
 *
 * @param {Element | null} trigger Trigger handed to the open call, if any.
 */
function recordFocusReturnTarget( trigger: Element | null ): void {
	focusReturnTarget = null;
	if ( trigger && trigger.isConnected ) {
		focusReturnTarget = trigger;
		return;
	}
	const userActivation = (
		navigator as Navigator & { userActivation?: { isActive: boolean } }
	 ).userActivation;
	if ( ! userActivation?.isActive ) {
		return;
	}
	const active = document.body.ownerDocument.activeElement;
	if (
		! active ||
		active === document.body ||
		active === document.documentElement ||
		active === iframe ||
		( overlay && overlay.contains( active ) )
	) {
		return;
	}
	focusReturnTarget = active;
}

/**
 * Give focus back after the drawer has left: the recorded target, else the
 * launcher inside the iframe, else nothing. Runs after the close delay because
 * Radix returns focus into the iframe when the drawer content unmounts, which
 * would overwrite a host focus call made earlier.
 */
function restoreFocus(): void {
	const target = focusReturnTarget;
	focusReturnTarget = null;
	restoreFocusPending = false;

	if (
		target &&
		target.isConnected &&
		! target.closest( '[inert], [hidden]' )
	) {
		( target as HTMLElement ).focus( { preventScroll: true } );
		if ( document.body.ownerDocument.activeElement === target ) {
			return;
		}
	}
	if ( isBubbleVisible ) {
		postToIframe( { type: MESSAGE_PREFIX, action: 'focus-launcher' } );
		return;
	}
	// No launcher to return to: the iframe app renders one whatever the host
	// says, but the collapsed frame is inert here.
	focusDocumentBody();
}

/**
 * Park focus on `document.body` without scrolling, for a page whose trigger hid
 * itself and that has no launcher. A temporary `tabindex="-1"` makes the body
 * focusable; failing that, the active element is blurred.
 */
function focusDocumentBody(): void {
	const body = document.body;
	const hadTabindex = body.hasAttribute( 'tabindex' );
	if ( ! hadTabindex ) {
		body.setAttribute( 'tabindex', '-1' );
	}
	body.focus( { preventScroll: true } );
	if ( ! hadTabindex ) {
		body.removeAttribute( 'tabindex' );
	}
	const active = body.ownerDocument.activeElement;
	if ( active && active !== body ) {
		( active as HTMLElement ).blur?.();
	}
}

/**
 * On a page without a bubble the closed iframe is a 0 x 0 box that still holds
 * the app's launcher button. Keep it out of the tab order and the accessibility
 * tree while closed; the visible-bubble case needs none of this.
 *
 * @param {boolean} inert Whether the iframe is collapsed.
 */
function setCollapsedIframeInert( inert: boolean ): void {
	if ( ! iframe || isBubbleVisible ) {
		return;
	}
	if ( inert ) {
		iframe.setAttribute( 'inert', '' );
		iframe.setAttribute( 'tabindex', '-1' );
	} else {
		iframe.removeAttribute( 'inert' );
		iframe.removeAttribute( 'tabindex' );
	}
}

/**
 * Is the chat modal in this state? Everything but the page-shift sidebar
 * (sidebar at 1500px or wider, where the page stays usable beside it) is.
 *
 * @param {DrawerState} state Current drawer state.
 */
function isModalState( state: DrawerState ): boolean {
	// The inline chat is part of the page: no inert, no Escape (03 section 5.5).
	if ( state === 'closed' || state === 'inline' ) {
		return false;
	}
	if ( state !== 'sidebar' ) {
		return true;
	}
	return ! (
		typeof window.matchMedia === 'function' &&
		window.matchMedia( PAGE_SHIFT_QUERY ).matches
	);
}

/**
 * Keep a banner or dialog the theme wants operable out of the inert set:
 * elements in the top layer, elements carrying the exclude attribute, and any
 * sibling that contains one (inert on an ancestor would disable it anyway).
 *
 * @param {Element} element Candidate sibling.
 */
function isInertExcluded( element: Element ): boolean {
	const attribute = `[${ INERT_EXCLUDE_ATTRIBUTE }]`;
	try {
		const selector = `${ attribute }, dialog:modal, :popover-open`;
		return (
			element.matches( selector ) || !! element.querySelector( selector )
		);
	} catch {
		// Engines without `:modal` or `:popover-open` reject the selector.
		return (
			element.matches( attribute ) ||
			!! element.querySelector( attribute )
		);
	}
}

/**
 * The host container and its ancestors up to `body`: siblings of any of them
 * are the rest of the page.
 */
function getChatPath(): Element[] {
	const path: Element[] = [];
	let node: Element | null = hostElement;
	while ( node && node !== document.documentElement ) {
		path.push( node );
		if ( node === document.body ) {
			break;
		}
		node = node.parentElement;
	}
	return path;
}

function canMakeInert( element: Element, path: Element[] ): boolean {
	return (
		! path.includes( element ) &&
		element !== overlay &&
		! INERT_SKIP_TAGS.has( element.tagName ) &&
		! isInertExcluded( element )
	);
}

function makeInert( element: Element ): void {
	if ( inertRecords.has( element ) ) {
		return;
	}
	if ( 'inert' in HTMLElement.prototype ) {
		// The theme's own `inert` stays the theme's.
		if ( ( element as HTMLElement ).inert ) {
			return;
		}
		( element as HTMLElement ).inert = true;
		inertRecords.set( element, { mode: 'inert', ariaHidden: null } );
		return;
	}
	const previous = element.getAttribute( 'aria-hidden' );
	if ( previous === 'true' ) {
		return;
	}
	element.setAttribute( 'aria-hidden', 'true' );
	inertRecords.set( element, { mode: 'aria-hidden', ariaHidden: previous } );
}

/**
 * Make the rest of the host page inert while a modal variant is open. Idempotent;
 * records what it set, so only that is removed again.
 */
function applyInert(): void {
	if ( inertObserver || ! hostElement ) {
		return;
	}
	const path = getChatPath();
	path.forEach( ( node ) => {
		const parent = node.parentElement;
		if ( ! parent || node === document.documentElement ) {
			return;
		}
		Array.from( parent.children ).forEach( ( sibling ) => {
			if ( canMakeInert( sibling, path ) ) {
				makeInert( sibling );
			}
		} );
	} );

	// Banners and widgets inserted while the chat is open must not stay reachable.
	inertObserver = new MutationObserver( ( mutations ) => {
		const currentPath = getChatPath();
		mutations.forEach( ( mutation ) => {
			mutation.addedNodes.forEach( ( added ) => {
				if (
					added.nodeType === Node.ELEMENT_NODE &&
					canMakeInert( added as Element, currentPath )
				) {
					makeInert( added as Element );
				}
			} );
		} );
	} );
	const observed = new Set< Element >();
	path.forEach( ( node ) => {
		if ( node.parentElement && ! observed.has( node.parentElement ) ) {
			observed.add( node.parentElement );
			inertObserver?.observe( node.parentElement, { childList: true } );
		}
	} );

	// Without `inert` the siblings are only hidden from assistive technology, so
	// focus that lands in one goes straight back into the chat.
	if ( ! ( 'inert' in HTMLElement.prototype ) ) {
		inertFocusGuard = ( event: FocusEvent ) => {
			const target = event.target as Element | null;
			if (
				target instanceof Element &&
				Array.from( inertRecords.keys() ).some( ( hidden ) =>
					hidden.contains( target )
				)
			) {
				iframe?.focus();
			}
		};
		document.addEventListener( 'focusin', inertFocusGuard, true );
	}
}

function removeInert(): void {
	inertObserver?.disconnect();
	inertObserver = null;
	if ( inertFocusGuard ) {
		document.removeEventListener( 'focusin', inertFocusGuard, true );
		inertFocusGuard = null;
	}
	inertRecords.forEach( ( record, element ) => {
		if ( record.mode === 'inert' ) {
			( element as HTMLElement ).inert = false;
		} else if ( record.ariaHidden === null ) {
			element.removeAttribute( 'aria-hidden' );
		} else {
			element.setAttribute( 'aria-hidden', record.ariaHidden );
		}
	} );
	inertRecords.clear();
}

/**
 * Escape on the host window asks the chat to close. Bubble phase on purpose: a
 * theme dropdown or lightbox that handles Escape (and calls `preventDefault()`
 * or `stopPropagation()`) gets the key first, and the chat stays open.
 *
 * @param {KeyboardEvent} event The keydown event.
 */
function handleHostEscape( event: KeyboardEvent ): void {
	if (
		event.key !== 'Escape' ||
		event.defaultPrevented ||
		event.isComposing ||
		currentState === 'closed'
	) {
		return;
	}
	if ( iframe && isChatReady ) {
		sendToIframe( iframe, {
			type: MESSAGE_PREFIX,
			action: 'close-request',
		} );
		return;
	}
	// Nothing could answer: close the host side directly.
	setIframeState( 'closed' );
}

function setEscapeListener( attached: boolean ): void {
	if ( attached === escListenerAttached ) {
		return;
	}
	if ( attached ) {
		window.addEventListener( 'keydown', handleHostEscape );
	} else {
		window.removeEventListener( 'keydown', handleHostEscape );
	}
	escListenerAttached = attached;
}

function handlePageShiftChange(): void {
	syncModalState();
}

/**
 * Bring inert and the Escape listener in line with the current state. Called on
 * every state change and when the sidebar crosses the page-shift width.
 */
function syncModalState(): void {
	if ( isModalState( currentState ) ) {
		applyInert();
		setEscapeListener( true );
	} else {
		removeInert();
		setEscapeListener( false );
	}
}

/**
 * While open, follow the page-shift width so the sidebar turns modal (or not)
 * when the window is resized or rotated. Ends when the chat closes.
 *
 * @param {boolean} observe Whether to observe the width.
 */
function observePageShift( observe: boolean ): void {
	if (
		observe &&
		! pageShiftQuery &&
		typeof window.matchMedia === 'function'
	) {
		pageShiftQuery = window.matchMedia( PAGE_SHIFT_QUERY );
		pageShiftQuery.addEventListener?.( 'change', handlePageShiftChange );
	} else if ( ! observe && pageShiftQuery ) {
		pageShiftQuery.removeEventListener?.( 'change', handlePageShiftChange );
		pageShiftQuery = null;
	}
}

/**
 * The iframe title names the assistant: "<header title> – Chatfenster".
 */
function applyIframeTitle(): void {
	if ( ! iframe ) {
		return;
	}
	const title =
		hostElement?.getAttribute( 'data-header-title' )?.trim() ||
		DEFAULT_HEADER_TITLE;
	iframe.setAttribute( 'title', `${ title } – Chatfenster` );
}

// ---------------------------------------------------------------------------
// iFrame Resize State Machine
// ---------------------------------------------------------------------------

/**
 * Source of an open that started inside the iframe: the bubble, or the sticky
 * bar when the host's own launcher state says the bar is what is on screen.
 */
function deriveIframeOpenMeta(): OpenMeta {
	return {
		source:
			isStickyBarMode() && currentLauncherState === 'bar'
				? 'sticky_bar'
				: 'bubble',
		hasPrompt: false,
	};
}

function setIframeState(
	state: DrawerState,
	trigger: Element | null = null,
	meta: OpenMeta | null = null
): void {
	if ( ! iframe ) {
		return;
	}

	// Clear any pending close timer
	if ( closeTimer ) {
		clearTimeout( closeTimer );
		closeTimer = null;
	}

	// Another surface takes the chat over (an open naming a variant, the
	// size toggle): the inline slot springs back on its own.
	if ( inlineSlot && state !== 'inline' && state !== 'closed' ) {
		releaseInlineSlot();
	}

	const wasClosed = currentState === 'closed';
	currentState = state;
	const startsSession = wasClosed && state !== 'closed';
	if ( state === 'closed' ) {
		track( ( activeTracker ) => activeTracker.onClose() );
		emit( 'close' );
		sessionMeta = null;
		// A question for a chat the visitor dismissed is never sent later.
		dropQueuedPrompt();
		unseenHostSends = 0;
	} else {
		if ( startsSession ) {
			sessionMeta = meta ?? deriveIframeOpenMeta();
		}
		const session = sessionMeta ?? deriveIframeOpenMeta();
		const payload: DhsvChatOpenPayload = {
			source: session.source,
			variant: state,
			...( session.topic ? { topic: session.topic } : {} ),
			hasPrompt: session.hasPrompt,
			sessionStart: startsSession,
			...( session.entry ? { entry: session.entry } : {} ),
			...( session.morph ? { morph: true } : {} ),
		};
		track( ( activeTracker ) => activeTracker.onOpen( payload ) );
		emit( 'open', payload );
	}
	if ( startsSession ) {
		// The collapsed iframe of a page without a bubble is out of reach
		// while closed; opening hands it focus again.
		setCollapsedIframeInert( false );
	}

	// Leaving `closed` starts a session. Its focus-return target is recorded
	// once: a variant change, a second open request, and a reopen during the
	// close delay all keep the first one.
	if ( startsSession && ! restoreFocusPending ) {
		recordFocusReturnTarget( trigger );
	}
	if ( startsSession ) {
		restoreFocusPending = true;
		applyIframeTitle();
		// After the focus-return target was recorded: the teaser may be it.
		onTeaserSessionStart();
	}

	// Modal variants make the page inert and listen for Escape; the page-shift
	// sidebar leaves the page alone. Closing undoes both at once, before the
	// drawer has finished sliding out, so the page is usable as soon as it shows.
	observePageShift( state !== 'closed' );
	syncModalState();

	switch ( state ) {
		case 'closed':
			// Hide overlay immediately
			hideOverlay();
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );

			// Inline: the slot springs back to the field first; the close
			// finishes once it landed and the app dropped its surface.
			if ( inlineSlot?.isAttached && ! inlineSlot.isClosing ) {
				inlineSlot.collapse( endInlineCollapse );
				break;
			}

			// Wait for the vaul drawer close animation to finish,
			// then shrink the iframe back to toggle size. A morph surface
			// flies back to its origin first: shrink once it has landed
			// (at once if `morph-landed` came before `drawer-close`), with a
			// safety timeout instead of the fixed drawer duration.
			closeTimer = setTimeout( finishClose, closeDelayMs() );
			break;

		case 'default':
			// Instantly expand to full viewport -- vaul handles the drawer animation
			expandIframe( startsSession );
			showOverlay( 'default' );
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			break;

		case 'sidebar':
			// Instantly expand to full viewport -- vaul handles the slide animation
			expandIframe( startsSession );
			showOverlay( 'sidebar' );
			document.documentElement.classList.add( SIDEBAR_OPEN_CLASS );
			break;

		case 'fullscreen':
			// Instantly expand to full viewport -- vaul handles the slide animation
			expandIframe( startsSession );
			showOverlay( 'fullscreen' );
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			break;

		case 'inline':
			// Part of the page: no overlay, no scroll lock, no inert.
			hideOverlay();
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			attachInlineSlot( startsSession );
			break;

		case 'island':
			// The island is a floating card, so the page stays *visible* around
			// it -- but not competing for attention. It gets the same plain
			// settings backdrop as the other variants (nothing at opacity 0).
			expandIframe( startsSession );
			showOverlay( 'island' );
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			break;
	}
}

/**
 * How long a close waits before shrinking the iframe. Unchanged for every
 * non-morph session; a morph that started waits for its landing.
 */
function closeDelayMs(): number {
	const session = morphSession;
	if ( ! session || ! session.started ) {
		return DRAWER_ANIMATION_MS;
	}
	return session.landed ? 0 : morphLandTimeoutMs;
}

/** The close animation is over: shrink, release the page, return focus. */
function finishClose(): void {
	if ( closeTimer ) {
		clearTimeout( closeTimer );
	}
	closeTimer = null;
	shrinkIframe();
	// Before focus is restored: an inert iframe cannot take it.
	setCollapsedIframeInert( true );
	// Undone here rather than up front: releasing the scroll lock
	// scrolls the page back to where the visitor left it, and doing
	// that under a drawer that is still sliding out reads as a jump.
	exitMobileFullscreen();
	// The origin is visible again before focus goes back to it.
	endMorphSession();
	// Sticky bar: the launcher state is frozen while the chat is open. The
	// page may have scrolled meanwhile, so the launcher the chat landed on
	// (bar or circle) catches up now instead of on the next scroll.
	pushLauncherState();
	if ( restoreFocusPending ) {
		restoreFocus();
	}
}

function endMorphSession(): void {
	const session = morphSession;
	morphSession = null;
	session?.end();
}

// ---------------------------------------------------------------------------
// iFrame Creation
// ---------------------------------------------------------------------------

function createIframe( hostContainer: HTMLElement ): HTMLIFrameElement {
	const el = document.createElement( 'iframe' );
	el.id = IFRAME_ID;

	// A fixed, page-independent URL from PHP, so page and browser caches can
	// serve every page from one document. A host page cached before that
	// attribute existed falls back to the old per-page URL.
	const fixedSrc = hostContainer.getAttribute( 'data-iframe-src' );
	if ( fixedSrc ) {
		el.src = fixedSrc;
	} else {
		const url = new URL( window.location.href );
		url.searchParams.set( 'dhsv100_chat_iframe', '1' );
		// Remove hash to avoid scrolling issues
		url.hash = '';
		el.src = url.toString();
	}

	el.style.border = 'none';
	el.style.background = 'transparent';
	el.style.zIndex = '2147483639';
	el.style.colorScheme = 'none';
	// NO transitions -- iframe resizes instantly; vaul provides the animation

	// Permissions
	el.setAttribute( 'allow', 'clipboard-write' );
	// The accessible name of the frame; set from the container's header title
	// (`applyIframeTitle()` repeats it when the chat opens).
	el.setAttribute(
		'title',
		`${
			hostContainer.getAttribute( 'data-header-title' )?.trim() ||
			DEFAULT_HEADER_TITLE
		} – Chatfenster`
	);

	return el;
}

/**
 * Create the iframe, once, the first time anything reaches for the chat.
 *
 * It stays hidden until the app inside reports 'chat-ready': until then the
 * placeholder is the launcher, and a half-loaded iframe on top of it would
 * swallow the click that is meant to open the chat.
 */
function ensureIframe(): void {
	if ( iframe || ! hostElement ) {
		return;
	}
	iframe = createIframe( hostElement );
	iframe.style.visibility = 'hidden';
	hostElement.appendChild( iframe );
	applyCollapsedIframeGeometry();
	setCollapsedIframeInert( currentState === 'closed' );
}

// ---------------------------------------------------------------------------
// Launcher placeholder
// ---------------------------------------------------------------------------

const SEND_ICON_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>';

/**
 * Paint the collapsed launcher in the host document.
 *
 * A static copy of the first frame the React launcher renders -- same sizes
 * from `bubble-sizes.ts`, same launcher tokens -- so the handoff on
 * 'chat-ready' is a swap, not a jump. It carries no animation of its own: the
 * morph between bar and bubble belongs to the real launcher.
 *
 * @param {HTMLElement} hostContainer The `#dhsv100-chat-host` element.
 */
function createPlaceholder( hostContainer: HTMLElement ): void {
	const sizeKey =
		hostContainer.getAttribute( 'data-bubble-size' ) || DEFAULT_BUBBLE_SIZE;
	const iconSrc =
		hostContainer.getAttribute( 'data-toggle-icon' ) || TOGGLE_ICON;
	const placeholderText =
		hostContainer.getAttribute( 'data-placeholder' ) || '';

	const css = getBubbleSizeCss( sizeKey );
	const bubblePx = getBubbleButtonPx( sizeKey );
	const borderPx = parseInt( css.border, 10 ) || 4;
	const marginPx = parseFloat( css.margin ) * 16;

	// Mirrors the pill metrics in `Composer.tsx`.
	const barPx = getStickyBarPx( sizeKey );
	const padPx = Math.max( 4, Math.round( barPx * 0.1 ) );
	const pillIconPx = Math.max( 22, barPx - 2 * padPx );
	const fontPx = Math.max( 12, Math.min( 16, Math.round( bubblePx * 0.2 ) ) );
	const gapPx = Math.max( 6, Math.round( bubblePx * 0.1 ) );

	const root = document.createElement( 'div' );
	root.className = PLACEHOLDER_CLASS;
	root.style.setProperty( '--dhsv100-ph-bubble', `${ bubblePx }px` );
	root.style.setProperty( '--dhsv100-ph-border', `${ borderPx }px` );
	root.style.setProperty( '--dhsv100-ph-margin', `${ marginPx }px` );
	root.style.setProperty( '--dhsv100-ph-bar', `${ barPx }px` );
	root.style.setProperty( '--dhsv100-ph-pad', `${ padPx }px` );
	root.style.setProperty( '--dhsv100-ph-pill-icon', `${ pillIconPx }px` );
	root.style.setProperty( '--dhsv100-ph-font', `${ fontPx }px` );
	root.style.setProperty( '--dhsv100-ph-gap', `${ gapPx }px` );

	const makeBubble = ( className: string ): HTMLButtonElement => {
		const button = document.createElement( 'button' );
		button.type = 'button';
		button.className = className;
		button.setAttribute( 'aria-label', 'Chat öffnen' );
		const img = document.createElement( 'img' );
		img.src = iconSrc;
		img.alt = '';
		button.appendChild( img );
		button.addEventListener( 'click', () => {
			// In sticky-bar mode the app picks the variant from the launcher
			// state itself, so 'default' is only the bubble-mode choice.
			requestOpen(
				{ variant: 'default', prompt: '', source: 'placeholder' },
				button,
				{ busyElement: null }
			);
		} );
		return button;
	};

	// Bubble mode (and every viewport below the sticky-bar breakpoint).
	const bubble = makeBubble( `${ PLACEHOLDER_CLASS }__bubble` );

	const pill = document.createElement( 'form' );
	pill.className = `${ PLACEHOLDER_CLASS }__pill`;
	const pillIcon = document.createElement( 'span' );
	pillIcon.className = `${ PLACEHOLDER_CLASS }__pill-icon`;
	pillIcon.setAttribute( 'aria-hidden', 'true' );
	const pillImg = document.createElement( 'img' );
	pillImg.src = iconSrc;
	pillImg.alt = '';
	pillIcon.appendChild( pillImg );
	const input = document.createElement( 'input' );
	input.type = 'text';
	input.name = 'message';
	input.autocomplete = 'off';
	input.spellcheck = false;
	input.placeholder = placeholderText;
	input.setAttribute( 'aria-label', placeholderText || 'Chat' );
	const send = document.createElement( 'button' );
	send.type = 'submit';
	send.className = `${ PLACEHOLDER_CLASS }__send`;
	send.disabled = true;
	send.setAttribute( 'aria-label', 'Nachricht senden' );
	send.innerHTML = SEND_ICON_SVG;
	input.addEventListener( 'input', () => {
		send.disabled = ! input.value.trim();
	} );
	pill.append( pillIcon, input, send );
	pill.addEventListener( 'click', ( event ) => {
		if ( event.target !== send ) {
			input.focus();
		}
	} );
	pill.addEventListener( 'submit', ( event ) => {
		event.preventDefault();
		const text = input.value.trim();
		if ( ! text ) {
			// Like an empty submit in the bar: open the panel.
			requestOpen(
				{ variant: 'island', prompt: '', source: 'placeholder' },
				pill,
				{ busyElement: null }
			);
			return;
		}
		input.value = '';
		send.disabled = true;
		openWithMessageAs(
			text,
			{ variant: 'island', startNewChat: false },
			pill,
			'placeholder'
		);
	} );

	// Sticky-bar mode: one shell that morphs between the pill and the circle,
	// like `Launcher.tsx` -- the pill is clipped from the left as the shell
	// narrows and slides right, the circle fades in over the last third.
	const stage = document.createElement( 'div' );
	stage.className = `${ PLACEHOLDER_CLASS }__stage`;
	const shell = document.createElement( 'div' );
	shell.className = `${ PLACEHOLDER_CLASS }__shell`;
	const shellBubble = makeBubble( `${ PLACEHOLDER_CLASS }__shell-bubble` );
	shell.append( pill, shellBubble );
	stage.appendChild( shell );

	root.append( bubble, stage );
	// No morph for the state the page loads in -- the real launcher also
	// starts without one.
	root.classList.add( `${ PLACEHOLDER_CLASS }--static` );

	// Anything that suggests the visitor is about to use the chat starts
	// loading it; hover buys the bootstrap a head start before the click.
	root.addEventListener( 'pointerenter', ensureIframe );
	root.addEventListener( 'pointerdown', ensureIframe );
	root.addEventListener( 'focusin', ensureIframe );
	root.addEventListener( 'touchstart', ensureIframe, { passive: true } );

	hostContainer.appendChild( root );
	placeholder = root;
	placeholderInput = input;
	updatePlaceholderState();
	requestAnimationFrame( () => {
		requestAnimationFrame( () => {
			root.classList.remove( `${ PLACEHOLDER_CLASS }--static` );
		} );
	} );
}

function updatePlaceholderState(): void {
	if ( ! placeholder ) {
		return;
	}
	const mode = isStickyBarMode() ? 'sticky' : 'bubble';
	if ( placeholder.dataset.mode && placeholder.dataset.mode !== mode ) {
		// Crossing the breakpoint swaps layouts outright; there is nothing to
		// morph between a bottom strip and a corner button.
		placeholder.classList.add( `${ PLACEHOLDER_CLASS }--static` );
		const el = placeholder;
		requestAnimationFrame( () => {
			requestAnimationFrame( () => {
				el.classList.remove( `${ PLACEHOLDER_CLASS }--static` );
			} );
		} );
	}
	placeholder.dataset.mode = mode;
	placeholder.dataset.state =
		mode === 'sticky' && currentLauncherState === 'bar' ? 'bar' : 'bubble';
}

function setPlaceholderBusy( busy: boolean ): void {
	if ( ! placeholder ) {
		return;
	}
	if ( busy ) {
		placeholder.setAttribute( 'aria-busy', 'true' );
	} else {
		placeholder.removeAttribute( 'aria-busy' );
	}
}

/**
 * Hand the launcher over to the iframe: show it, drop the placeholder, and
 * pass on anything the visitor typed into it in the meantime.
 */
function revealIframe(): void {
	if ( ! iframe ) {
		return;
	}
	iframe.style.visibility = '';

	if ( ! placeholder ) {
		return;
	}
	const text = placeholderInput?.value ?? '';
	const hadFocus =
		!! placeholderInput &&
		placeholderInput.ownerDocument.activeElement === placeholderInput;
	placeholder
		.querySelectorAll( 'button, form' )
		.forEach( ( control ) =>
			handoffRects.set( control, control.getBoundingClientRect() )
		);
	placeholder.remove();
	placeholder = null;
	placeholderInput = null;
	// The iframe takes the strip over; the teaser now measures that.
	positionTeaser();

	if ( text || hadFocus ) {
		if ( hadFocus ) {
			iframe.focus();
		}
		sendToIframe( iframe, {
			type: MESSAGE_PREFIX,
			action: 'set-draft',
			text,
			focus: hadFocus,
		} );
	}
}

function sendPageContext(): void {
	if ( ! iframe || ! hostElement ) {
		return;
	}
	const pageId = parseInt(
		hostElement.getAttribute( 'data-page-id' ) || '0',
		10
	);
	sendToIframe( iframe, {
		type: MESSAGE_PREFIX,
		action: 'page-context',
		pageId: Number.isFinite( pageId ) ? pageId : 0,
		...( activeSection ? { sectionId: activeSection.id } : {} ),
		hostCapabilities: [ 'launcher-morph' ],
	} );
}

// ---------------------------------------------------------------------------
// Message Handler
// ---------------------------------------------------------------------------

function handleMessage( event: MessageEvent ): void {
	// Only accept messages from our iframe
	if (
		! iframe ||
		event.source !== iframe.contentWindow ||
		! isTrustedOrigin( event )
	) {
		return;
	}

	if ( ! isIframeToHostMessage( event.data ) ) {
		return;
	}

	const msg: IframeToHostMessage = event.data;

	switch ( msg.action ) {
		case 'ready':
			// iFrame is loaded and React app is mounted.
			if ( msg.capabilities ) {
				iframeCapabilities = sanitizeCapabilities( msg.capabilities );
			}
			break;

		case 'chat-ready':
			// Idempotent: a repeat 'chat-ready' leaves the flag set and simply
			// finds nothing pending to drain.
			isChatReady = true;
			iframeCapabilities = sanitizeCapabilities( msg.capabilities );
			clearPendingCommandTimer();
			// The page first: a replayed command may depend on it.
			sendPageContext();
			revealIframe();
			// 'ready' is emitted BEFORE draining, so a subscriber that reveals
			// its launcher on 'ready' and hides it on 'open' sees them in that
			// order -- the replayed command emits 'open' synchronously.
			emit( 'ready' );
			runPendingCommand();
			pushLauncherState( true );
			break;

		case 'drawer-open':
			// `inline` only confirms an inline open the host started.
			if ( msg.variant === 'inline' ) {
				if ( inlineSlot && ! inlineSlot.isClosing ) {
					setIframeState( 'inline' );
				}
				break;
			}
			// Additive `morph`: the open morphs out of the iframe's launcher.
			setIframeState(
				msg.variant,
				null,
				msg.morph === true && currentState === 'closed'
					? { ...deriveIframeOpenMeta(), morph: true }
					: null
			);
			break;

		case 'drawer-close':
			setIframeState( 'closed' );
			break;

		case 'inline-ended':
			completeInlineEnd();
			break;

		case 'messages-changed':
			messagesCache = msg.messages;
			trackUserMessages( messagesCache );
			emit( 'messages-changed', messagesCache );
			if ( messagesCache.length > previousMessageCount ) {
				emit( 'message', messagesCache[ messagesCache.length - 1 ] );
			}
			previousMessageCount = messagesCache.length;
			break;

		case 'status-changed': {
			const previousStatus = statusCache;
			statusCache = msg.status;
			// A failed answer, or a request that ended before any text
			// (Stop while waiting), breaks the context the question was
			// waiting for: the visitor decides what to ask next.
			if (
				statusCache === 'error' ||
				( statusCache === 'idle' && previousStatus === 'submitted' )
			) {
				dropQueuedPrompt();
			}
			emit( 'status-changed', statusCache );
			break;
		}

		case 'response-done':
			emit( 'response-done' );
			flushQueuedPrompt( msg.aborted === true );
			break;

		case 'track':
			track( ( activeTracker ) => activeTracker.onTrack( msg ) );
			break;

		case 'conversation-changed':
			track( ( activeTracker ) =>
				activeTracker.onConversationChanged( msg.conversationId )
			);
			break;

		case 'morph-started':
			// No host session waiting: the iframe morphs out of its own
			// launcher. Track the landing so the shrink waits for it.
			if ( ! morphSession || morphSession.landed ) {
				endMorphSession();
				const flags = resolveMorphMotionFlags(
					readMotionEnvironment()
				);
				morphLandTimeoutMs =
					MORPH_LAND_TIMEOUT_MS * Math.max( 1, flags.slow ?? 1 );
				morphSession = beginLandingSession();
			}
			morphSession.start();
			break;

		case 'morph-landed':
			morphSession?.land();
			// `drawer-close` came first: the shrink was waiting for this.
			if ( currentState === 'closed' && closeTimer && morphSession ) {
				finishClose();
			}
			break;

		case 'surface-rect':
			// The surface is on its way back: the backdrop fades with it
			// rather than after the landing.
			if (
				morphSession &&
				isSurfaceRectMessage( msg ) &&
				msg.phase === 'closing'
			) {
				hideOverlay();
			}
			break;

		case 'launcher-height':
			if (
				typeof msg.height !== 'number' ||
				! Number.isFinite( msg.height )
			) {
				break;
			}
			stickyBarContentHeightPx = Math.max( 0, Math.round( msg.height ) );
			// While a close is still animating, the collapsed launcher is already
			// mounted and reports its height. Collapsing the iframe here would cut
			// the closing drawer off mid-animation, so leave that to the pending
			// close timer, which picks up the height recorded above.
			if (
				currentState === 'closed' &&
				! closeTimer &&
				isStickyBarMode()
			) {
				applyCollapsedIframeGeometry();
				positionTeaser();
			}
			break;
	}
}

function postToIframe(
	message:
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'open-request';
				variant?: DrawerVariant;
				explicitVariant?: boolean;
				origin?: HostOpenOrigin;
				morph?: HostMorphOpen;
				inline?: HostInlineOpen;
				draft?: string;
				source?: EntrySource | 'placeholder';
		  }
		| { type: typeof MESSAGE_PREFIX; action: 'close-request' }
		| { type: typeof MESSAGE_PREFIX; action: 'focus-launcher' }
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'open-with-message';
				text: string;
				variant?: DrawerVariant;
				explicitVariant?: boolean;
				startNewChat?: boolean;
				origin?: HostOpenOrigin;
				morph?: HostMorphOpen;
				inline?: HostInlineOpen;
				source?: EntrySource;
		  }
		| { type: typeof MESSAGE_PREFIX; action: 'send-message'; text: string }
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'queued-prompt';
				queued: boolean;
		  }
		| { type: typeof MESSAGE_PREFIX; action: 'new-chat' }
		| { type: typeof MESSAGE_PREFIX; action: 'clear-history' }
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'set-view';
				view: 'popover' | 'history';
		  }
): void {
	if ( ! iframe ) {
		return;
	}
	sendToIframe( iframe, message );
}

// ---------------------------------------------------------------------------
// Readiness Gate
// ---------------------------------------------------------------------------

function clearPendingCommandTimer(): void {
	if ( pendingCommandTimer ) {
		clearTimeout( pendingCommandTimer );
		pendingCommandTimer = null;
	}
}

/**
 * Run a command now if the iframe app is listening, otherwise hold it.
 *
 * Only one command is held: it is always the caller's most recent intent, so a
 * later close() supersedes an earlier open(). Replacement is unconditional and
 * does not inspect the command being replaced. The timeout restarts on each
 * deferral, keeping the deadline anchored to the latest intent.
 *
 * @param {string}   label Command name, used in the timeout warning.
 * @param {Function} run   The command to run now or on 'chat-ready'.
 * @param            hooks
 */
function deferUntilReady(
	label: string,
	run: () => void,
	hooks: CommandHooks = {}
): void {
	if ( isChatReady ) {
		run();
		hooks.onDelivered?.();
		return;
	}

	// A command is the strongest signal there is that the chat is wanted.
	ensureIframe();
	setPlaceholderBusy( true );

	// Replacement is unconditional; the replaced command's trigger stops
	// waiting (its text, if any, is still its own to keep).
	const replaced = pendingCommand;
	if ( replaced ) {
		replaced.busyElement?.removeAttribute( 'aria-busy' );
		replaced.onReplaced?.();
	}
	hooks.busyElement?.setAttribute( 'aria-busy', 'true' );

	clearPendingCommandTimer();
	pendingCommand = { label, run, ...hooks };
	pendingCommandTimer = setTimeout( () => {
		const discarded = pendingCommand;
		pendingCommand = null;
		pendingCommandTimer = null;
		setPlaceholderBusy( false );
		if ( discarded ) {
			discarded.busyElement?.removeAttribute( 'aria-busy' );
			discarded.onDiscarded?.();
			// eslint-disable-next-line no-console
			console.warn(
				'dhsv100-ai-chat-plugin: the chat did not become ready within ' +
					CHAT_READY_TIMEOUT_MS +
					'ms; discarding pending "' +
					discarded.label +
					'" command'
			);
		}
	}, CHAT_READY_TIMEOUT_MS );
}

function runPendingCommand(): void {
	clearPendingCommandTimer();
	const command = pendingCommand;
	pendingCommand = null;
	if ( command ) {
		command.busyElement?.removeAttribute( 'aria-busy' );
		command.run();
		command.onDelivered?.();
	}
}

/**
 * Run an open call on behalf of a host-side trigger element (the launcher
 * placeholder), so the chat can grow from it. The trigger is captured by the
 * API call itself -- see `takeOpenTrigger()` -- and measured only when the open
 * actually runs. There is deliberately no "last clicked element" tracking:
 * a programmatic `DhsvChat.open()` carries no origin.
 * @param trigger
 * @param run
 */
function withOpenTrigger( trigger: Element, run: () => void ): void {
	openTrigger = trigger;
	try {
		run();
	} finally {
		openTrigger = null;
	}
}

function takeOpenTrigger(): Element | null {
	const trigger = openTrigger;
	openTrigger = null;
	return trigger;
}

/**
 * Centre of the trigger, clamped into the viewport, for the `origin` of an
 * open command. A zero-size or detached trigger yields nothing, and the field
 * is then omitted -- except a placeholder control handed over to the chat,
 * whose last position is remembered in `handoffRects`.
 * @param trigger
 */
function measureOpenOrigin( trigger: Element | null ): {
	origin?: HostOpenOrigin;
} {
	if ( ! trigger ) {
		return {};
	}
	// A detached trigger only has a position if it is a placeholder control
	// that was handed over to the chat; any other detached element has none.
	const rect = trigger.isConnected
		? trigger.getBoundingClientRect()
		: handoffRects.get( trigger );
	const origin = getRectCenter( rect, {
		width: window.innerWidth,
		height: window.innerHeight,
	} );
	return origin ? { origin } : {};
}

/**
 * Offset the expanded iframe sits at (`applyExpandedIframeGeometry()`): the
 * visual viewport offset in the mobile shell, zero otherwise.
 */
function currentViewportOffset(): { x: number; y: number } {
	const viewport = window.visualViewport;
	if ( viewport && shouldTrackVisualViewport() ) {
		return {
			x: Math.round( viewport.offsetLeft ),
			y: Math.round( viewport.offsetTop ),
		};
	}
	return { x: 0, y: 0 };
}

type MorphOpenParts = {
	/** Command fields: `morph` and `source`. */
	fields: { morph?: HostMorphOpen; source?: EntrySource };
	/** Open-event additions. */
	meta: { entry?: EntrySource; morph?: boolean };
};

/**
 * The morph half of an open from a `data-dhsv-chat-morph` trigger. Applies
 * only when the chat is closed and the iframe announced the `morph`
 * capability; anything else returns no `morph` and the open runs as before.
 * Measures the origin before touching its style, then starts the
 * anticipation -- so it must run in the same task as the `open-request`.
 *
 * @param {Element|null} trigger The element the chat opens from.
 */
function prepareMorphOpen( trigger: Element | null ): MorphOpenParts {
	if ( ! trigger || ! isMorphTrigger( trigger ) ) {
		return { fields: {}, meta: {} };
	}
	const entry = readEntrySource( trigger );
	const base: MorphOpenParts = entry
		? { fields: { source: entry }, meta: { entry } }
		: { fields: {}, meta: {} };
	if (
		currentState !== 'closed' ||
		! iframeSupportsMorph() ||
		! ( trigger instanceof HTMLElement )
	) {
		return base;
	}
	// A reopen during the previous landing: that origin is visible again
	// before it is measured.
	endMorphSession();
	// A cold-start press drops its held scale in this task, so the origin is
	// measured unscaled and the session holds the press without a jump.
	// eslint-disable-next-line @wordpress/no-unused-vars-before-return -- must run before measuring
	const held = takeColdStartPress( trigger );
	const measured = measureMorphOrigin( trigger );
	if ( ! measured ) {
		return base;
	}
	const flags = resolveMorphMotionFlags( readMotionEnvironment() );
	morphLandTimeoutMs = MORPH_LAND_TIMEOUT_MS * Math.max( 1, flags.slow ?? 1 );
	const session = beginMorphOriginSession( {
		trigger,
		flags,
		held,
		sendRect: ( rect ) => {
			if ( iframe ) {
				sendToIframe( iframe, {
					type: MESSAGE_PREFIX,
					action: 'origin-rect',
					rect,
					viewportOffset: currentViewportOffset(),
				} );
			}
		},
	} );
	morphSession = session;
	const placement = readPlacement( trigger );
	const morph: HostMorphOpen = {
		presentation: 'morph',
		...( placement ? { placement } : {} ),
		originRect: measured.originRect,
		viewportOffset: currentViewportOffset(),
		skin: measured.skin,
		...( measured.snapshot ? { snapshot: measured.snapshot } : {} ),
		motion: { ...flags, pressed: session.pressed },
	};
	return {
		fields: { ...base.fields, morph },
		meta: { ...base.meta, morph: true },
	};
}

/**
 * Start the cold-start press when a morph trigger opens the chat before it is
 * ready (01 section 5). The open itself waits in the readiness gate. A repeat
 * activation of the same trigger keeps the running press.
 *
 * @param {Element|null} trigger The element the chat opens from.
 * @param {Object}       owner   The command the press now belongs to.
 */
function beginColdStart( trigger: Element | null, owner: object ): void {
	if (
		isChatReady ||
		currentState !== 'closed' ||
		! ( trigger instanceof HTMLElement ) ||
		! isMorphTrigger( trigger )
	) {
		return;
	}
	if ( coldStartPress?.press.trigger === trigger ) {
		coldStartPress.owner = owner;
		return;
	}
	coldStartPress?.press.release();
	coldStartPress = {
		press: beginColdStartPress(
			trigger,
			resolveMorphMotionFlags( readMotionEnvironment() )
		),
		owner,
	};
}

/**
 * Hand the trigger's cold-start press over to a morph session.
 *
 * @param {Element} trigger The element the morph starts from.
 * @return Whether the trigger is held at the anticipation scale.
 */
function takeColdStartPress( trigger: Element ): boolean {
	const current = coldStartPress;
	if ( ! current || current.press.trigger !== trigger ) {
		return false;
	}
	coldStartPress = null;
	return current.press.handOff();
}

/**
 * The open ran without a morph, or never ran: the press springs back.
 *
 * @param {Object} owner Only the press of this command.
 */
function releaseColdStartPress( owner: object ): void {
	const current = coldStartPress;
	if ( ! current || current.owner !== owner ) {
		return;
	}
	coldStartPress = null;
	current.press.release();
}

const API_OPEN_META: OpenMeta = { source: 'api', hasPrompt: false };

function openDrawer(
	variant: DrawerVariant = 'default',
	trigger: Element | null = null,
	meta: OpenMeta = API_OPEN_META,
	hostResolved = false,
	draft = ''
): void {
	// Measured first: opening removes the teaser, which may be the trigger.
	const origin = measureOpenOrigin( trigger );
	const morph = prepareMorphOpen( trigger );
	setIframeState( variant, trigger, { ...meta, ...morph.meta } );
	postToIframe( {
		type: MESSAGE_PREFIX,
		action: 'open-request',
		variant,
		...( hostResolved ? { explicitVariant: true } : {} ),
		...origin,
		// Additive: the launcher placeholder stood in for the iframe's own
		// launcher, so the chat may morph out of that launcher.
		...( meta.source === 'placeholder' ? { source: 'placeholder' } : {} ),
		...morph.fields,
		...( draft ? { draft } : {} ),
	} );
}

// ---------------------------------------------------------------------------
// Entry points: prompt delivery and the one-slot queue
// ---------------------------------------------------------------------------

function isAnswerRunning(): boolean {
	return statusCache === 'submitted' || statusCache === 'streaming';
}

function setQueuedPrompt( text: string | null ): void {
	const wasQueued = queuedPrompt !== null;
	queuedPrompt = text;
	const isQueued = text !== null;
	if ( wasQueued !== isQueued ) {
		postToIframe( {
			type: MESSAGE_PREFIX,
			action: 'queued-prompt',
			queued: isQueued,
		} );
	}
}

function dropQueuedPrompt(): void {
	if ( queuedPrompt !== null ) {
		setQueuedPrompt( null );
	}
}

/**
 * The answer is done: send the waiting question through the existing
 * `send-message`, unless the visitor stopped the answer.
 *
 * @param {boolean} aborted `response-done` carried `aborted: true`.
 */
function flushQueuedPrompt( aborted: boolean ): void {
	if ( queuedPrompt === null ) {
		return;
	}
	if ( aborted ) {
		dropQueuedPrompt();
		return;
	}
	if ( isAnswerRunning() ) {
		return;
	}
	const text = queuedPrompt;
	setQueuedPrompt( null );
	lastDelivered = { text, at: Date.now() };
	unseenHostSends += 1;
	postToIframe( { type: MESSAGE_PREFIX, action: 'send-message', text } );
}

/**
 * Follow the visitor's own questions: one the queue did not send (a user
 * message nobody on the host side posted) means "their question wins".
 *
 * @param {ChatMessage[]} messages The snapshot the iframe reported.
 */
function trackUserMessages( messages: ChatMessage[] ): void {
	const userCount = messages.filter( ( m ) => m.role === 'user' ).length;
	const delta = userCount - lastUserMessageCount;
	lastUserMessageCount = userCount;
	if ( delta < 0 ) {
		// A new chat or a cleared history starts counting over.
		unseenHostSends = 0;
		return;
	}
	if ( delta === 0 ) {
		return;
	}
	const ours = Math.min( delta, unseenHostSends );
	unseenHostSends -= ours;
	if ( delta > ours ) {
		dropQueuedPrompt();
	}
}

type PromptVerdict = 'send' | 'queue' | 'duplicate';

/**
 * Can the chat take this question right now? Read at run time, not at call
 * time: the status is whatever the iframe reported last.
 *
 * @param {string} prompt Normalised, non-empty prompt.
 */
function judgePrompt( prompt: string ): PromptVerdict {
	const recent =
		lastDelivered !== null &&
		Date.now() - lastDelivered.at < PROMPT_SETTLE_MS;
	if ( recent && lastDelivered?.text === prompt ) {
		return 'duplicate';
	}
	// A different question right behind another: the iframe has not started
	// submitting the first one yet, so the status does not show it.
	return isAnswerRunning() || recent ? 'queue' : 'send';
}

function focusIframeElement(): void {
	iframe?.focus();
}

/**
 * Run an entry-point open once the chat is ready.
 *
 * Without a prompt it only opens. With one, the question is sent as the
 * visitor's own message on the current conversation -- or queued behind a
 * running answer. A chat that is already open is never toggled closed: the
 * focus-return target stays, focus moves into the iframe.
 *
 * @param {OpenRequest}  request Normalised command.
 * @param {Element|null} trigger Element the chat grows from and focus returns to.
 */
function runEntryOpen( request: OpenRequest, trigger: Element | null ): void {
	const { variant, prompt } = request;
	const resolved = request.hostResolved === true;
	const meta: OpenMeta = {
		source: request.source,
		...( request.topic ? { topic: request.topic } : {} ),
		hasPrompt: prompt !== '',
	};

	if ( currentState !== 'closed' ) {
		// `open( 'fullscreen' )` on an open chat has always switched the
		// surface; a call that named no variant leaves it alone.
		if ( request.explicitVariant ) {
			openDrawer( variant, trigger, meta, resolved );
		}
		if ( prompt ) {
			const verdict = judgePrompt( prompt );
			if ( verdict === 'send' ) {
				lastDelivered = { text: prompt, at: Date.now() };
				unseenHostSends += 1;
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'send-message',
					text: prompt,
				} );
			} else if ( verdict === 'queue' ) {
				setQueuedPrompt( prompt );
			}
		}
		// Still inline (nothing named another surface): bring it into view.
		if ( currentState === 'inline' ) {
			inlineSlot?.reveal();
		}
		focusIframeElement();
		return;
	}

	const inline = prepareInlineOpen( request );
	if ( inline ) {
		runInlineOpen( request, trigger, meta, inline );
		return;
	}

	if ( ! prompt ) {
		openDrawer( variant, trigger, meta, resolved, request.draft );
		return;
	}

	const verdict = judgePrompt( prompt );
	if ( verdict === 'send' ) {
		lastDelivered = { text: prompt, at: Date.now() };
		unseenHostSends += 1;
		const origin = measureOpenOrigin( trigger );
		const morph = prepareMorphOpen( trigger );
		setIframeState( variant, trigger, { ...meta, ...morph.meta } );
		postToIframe( {
			type: MESSAGE_PREFIX,
			action: 'open-with-message',
			text: prompt,
			variant,
			...( resolved ? { explicitVariant: true } : {} ),
			startNewChat: false,
			...origin,
			...morph.fields,
		} );
		return;
	}
	openDrawer( variant, trigger, meta, resolved );
	if ( verdict === 'queue' ) {
		setQueuedPrompt( prompt );
	}
}

/**
 * The inline half of a closed-chat open from a `data-dhsv-chat-expand="inline"`
 * form: creates the slot (spacer, inert form) when the iframe can run inline.
 * Anything else (older iframe, form gone) opens the regular surface.
 *
 * @param {OpenRequest} request Normalised command.
 */
function prepareInlineOpen( request: OpenRequest ): HostInlineOpen | undefined {
	const form = request.inlineForm;
	if (
		! form ||
		! form.isConnected ||
		! iframe ||
		! iframeCapabilities.includes( 'inline' )
	) {
		return undefined;
	}
	// A slot still collapsing from the previous inline session lets go.
	releaseInlineSlot();
	const flags = resolveMorphMotionFlags( readMotionEnvironment() );
	const slot = new InlineChatSlot( form, {
		reducedMotion: flags.reducedMotion,
		slow: flags.slow,
		onOpened: () => slot.reveal(),
	} );
	inlineSlot = slot;
	return { radius: slot.panelRadius };
}

/**
 * Open the chat inline. The prompt is the visitor's first message, exactly as
 * for a regular open: sent at once, or queued behind a running answer.
 *
 * @param {OpenRequest}    request Normalised command.
 * @param {Element|null}   trigger Focus returns here after the collapse.
 * @param {OpenMeta}       meta    Open-event metadata.
 * @param {HostInlineOpen} inline  The `inline` field of the command.
 */
function runInlineOpen(
	request: OpenRequest,
	trigger: Element | null,
	meta: OpenMeta,
	inline: HostInlineOpen
): void {
	const { prompt } = request;
	const verdict = prompt ? judgePrompt( prompt ) : null;
	setIframeState( 'inline', trigger, meta );
	if ( verdict === 'send' ) {
		lastDelivered = { text: prompt, at: Date.now() };
		unseenHostSends += 1;
		postToIframe( {
			type: MESSAGE_PREFIX,
			action: 'open-with-message',
			text: prompt,
			startNewChat: false,
			inline,
		} );
		return;
	}
	postToIframe( {
		type: MESSAGE_PREFIX,
		action: 'open-request',
		inline,
		...( request.draft ? { draft: request.draft } : {} ),
	} );
	if ( verdict === 'queue' ) {
		setQueuedPrompt( prompt );
	}
}

/**
 * Entry point of every open that is not `openWithMessage()`: the public
 * `open()`, the delegated triggers, the block, the teaser and the placeholder.
 * The command waits for `chat-ready` like every other.
 *
 * @param {OpenRequest}  request Normalised command.
 * @param {Element|null} trigger Explicit trigger element, if any.
 * @param {CommandHooks} hooks   What the trigger wants to know about its command.
 */
function requestOpen(
	request: OpenRequest,
	trigger: Element | null,
	hooks: CommandHooks = {}
): void {
	const owner = {};
	beginColdStart( trigger, owner );
	deferUntilReady( 'open', () => runEntryOpen( request, trigger ), {
		busyElement: trigger,
		...hooks,
		// A morph took the press over in `prepareMorphOpen()`; an open that
		// did not morph (no capability, origin gone), a timeout or a later
		// command lets it go here.
		onDelivered: () => {
			releaseColdStartPress( owner );
			hooks.onDelivered?.();
		},
		onReplaced: () => {
			releaseColdStartPress( owner );
			hooks.onReplaced?.();
		},
		onDiscarded: () => {
			releaseColdStartPress( owner );
			hooks.onDiscarded?.();
		},
	} );
}

/**
 * `DhsvChat.open( variant | options )`. An absent or invalid variant opens
 * `default`: only the delegated entry points apply the surface rule.
 *
 * @param {unknown} arg A variant string or an options object.
 */
function apiOpen( arg?: unknown ): void {
	const parsed = parseOpenOptions( arg );
	requestOpen(
		{
			variant: parsed.variant === 'unset' ? 'default' : parsed.variant,
			explicitVariant: parsed.variant !== 'unset',
			hostResolved: parsed.variant !== 'unset',
			prompt: parsed.prompt,
			topic: parsed.topic,
			source: parsed.source ?? 'api',
		},
		takeOpenTrigger()
	);
}

/**
 * `openWithMessage()` keeps its semantics (sent at once, never queued); only
 * the attribution differs by caller.
 *
 * @param {string}                 text    The message.
 * @param {OpenWithMessageOptions} options Variant and `startNewChat`.
 * @param {Element|null}           trigger Explicit trigger element, if any.
 * @param {string}                 source  Attribution of the open.
 */
function openWithMessageAs(
	text: string,
	options: Required< OpenWithMessageOptions >,
	trigger: Element | null,
	source: string
): void {
	deferUntilReady( 'openWithMessage', () => {
		unseenHostSends += 1;
		const origin = measureOpenOrigin( trigger );
		setIframeState( options.variant, trigger, { source, hasPrompt: true } );
		postToIframe( {
			type: MESSAGE_PREFIX,
			action: 'open-with-message',
			text,
			variant: options.variant,
			startNewChat: options.startNewChat,
			...origin,
		} );
	} );
}

function parseOpenWithMessageOptions(
	options?: DrawerVariant | OpenWithMessageOptions
): Required< OpenWithMessageOptions > {
	if ( ! options ) {
		return { variant: 'default', startNewChat: false };
	}
	if ( typeof options === 'string' ) {
		return { variant: options, startNewChat: false };
	}
	return {
		variant: options.variant || 'default',
		startNewChat: options.startNewChat === true,
	};
}

function printHelp(): void {
	// eslint-disable-next-line no-console
	console.group(
		'%cDhsvChat API Reference',
		'font-weight:bold;font-size:14px;'
	);
	// eslint-disable-next-line no-console
	console.log( 'Drawer Control' );
	// eslint-disable-next-line no-console
	console.log( '  open(variant | options?)' );
	// eslint-disable-next-line no-console
	console.log(
		"                        Open the chat drawer. options: { prompt?: string, topic?: string, variant?: 'default'|'sidebar'|'fullscreen'|'island', source?: string }"
	);
	// eslint-disable-next-line no-console
	console.log(
		"                        A prompt is sent as the visitor's question, or queued behind a running answer."
	);
	// eslint-disable-next-line no-console
	console.log( '  close()               Close the chat drawer.' );
	// eslint-disable-next-line no-console
	console.log( '  toggle(variant?)      Toggle open and close.' );
	// eslint-disable-next-line no-console
	console.log( '  isOpen()              Returns true when open.' );
	// eslint-disable-next-line no-console
	console.log( 'Messaging' );
	// eslint-disable-next-line no-console
	console.log( '  openWithMessage(text, options?)' );
	// eslint-disable-next-line no-console
	console.log(
		"                        Open and submit message. options: { variant?: 'default'|'sidebar'|'fullscreen', startNewChat?: boolean }"
	);
	// eslint-disable-next-line no-console
	console.log( '  openWithNewMessage(text, variant?)' );
	// eslint-disable-next-line no-console
	console.log( '                        Shortcut for startNewChat=true.' );
	// eslint-disable-next-line no-console
	console.log( '  sendMessage(text)     Submit message without opening.' );
	// eslint-disable-next-line no-console
	console.log( 'Chat Lifecycle' );
	// eslint-disable-next-line no-console
	console.log( '  newChat()             Start a new chat.' );
	// eslint-disable-next-line no-console
	console.log( '  clearHistory()        Delete all chat history.' );
	// eslint-disable-next-line no-console
	console.log( '  openHistory()         Open chat history view.' );
	// eslint-disable-next-line no-console
	console.log( 'Read State' );
	// eslint-disable-next-line no-console
	console.log( '  getMessages()         Get cached messages.' );
	// eslint-disable-next-line no-console
	console.log( '  getStatus()           Get current status.' );
	// eslint-disable-next-line no-console
	console.log( 'Loading' );
	// eslint-disable-next-line no-console
	console.log(
		'  preload()             Start loading the chat now (e.g. on hover).'
	);
	// eslint-disable-next-line no-console
	console.log( 'Events' );
	// eslint-disable-next-line no-console
	console.log( '  on(event, cb), off(event, cb)' );
	// eslint-disable-next-line no-console
	console.log(
		'  Events: ready, open, close, message, messages-changed, status-changed, response-done'
	);
	// eslint-disable-next-line no-console
	console.log(
		"  on('ready', cb) fires immediately if the chat is already ready."
	);
	// eslint-disable-next-line no-console
	console.log( 'DOM Event' );
	// eslint-disable-next-line no-console
	console.log(
		"  window.addEventListener('" +
			DHSV_CHAT_READY_EVENT +
			"', e => e.detail.api)"
	);
	// eslint-disable-next-line no-console
	console.log(
		'                        Fires once when this API is installed. See docs/guides/custom-launcher.md'
	);
	// eslint-disable-next-line no-console
	console.groupEnd();
}

function registerApi(): void {
	window.DhsvChat = {
		open: apiOpen,
		close: () => {
			deferUntilReady( 'close', () => {
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'close-request',
				} );
			} );
		},
		toggle: ( variant = 'default' ) => {
			const trigger = takeOpenTrigger();
			deferUntilReady( 'toggle', () => {
				if ( currentState === 'closed' ) {
					openDrawer( variant, trigger, API_OPEN_META );
					return;
				}
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'close-request',
				} );
			} );
		},
		isOpen: () => currentState !== 'closed',
		openWithMessage: ( text, options ) => {
			// Validation runs at call time, ahead of the gate: a blank-text
			// no-op must not occupy the pending slot and evict a real command.
			if ( ! text.trim() ) {
				return;
			}
			openWithMessageAs(
				text,
				parseOpenWithMessageOptions( options ),
				takeOpenTrigger(),
				'api'
			);
		},
		openWithNewMessage: ( text, variant = 'default' ) => {
			if ( ! text.trim() ) {
				return;
			}
			const trigger = takeOpenTrigger();
			deferUntilReady( 'openWithNewMessage', () => {
				unseenHostSends += 1;
				const origin = measureOpenOrigin( trigger );
				setIframeState( variant, trigger, {
					source: 'api',
					hasPrompt: true,
				} );
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'open-with-message',
					text,
					variant,
					startNewChat: true,
					...origin,
				} );
			} );
		},
		sendMessage: ( text ) => {
			if ( ! text.trim() ) {
				return;
			}
			deferUntilReady( 'sendMessage', () => {
				unseenHostSends += 1;
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'send-message',
					text,
				} );
			} );
		},
		newChat: () => {
			deferUntilReady( 'newChat', () => {
				postToIframe( { type: MESSAGE_PREFIX, action: 'new-chat' } );
			} );
		},
		clearHistory: () => {
			// The cache reset happens at call time even while deferred:
			// getMessages() is ungated, so it must never hand back history the
			// caller has already cleared. Only the post is deferred.
			messagesCache = [];
			previousMessageCount = 0;
			lastUserMessageCount = 0;
			deferUntilReady( 'clearHistory', () => {
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'clear-history',
				} );
			} );
		},
		openHistory: () => {
			deferUntilReady( 'openHistory', () => {
				openDrawer();
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'set-view',
					view: 'history',
				} );
			} );
		},
		getMessages: () => messagesCache,
		getStatus: () => statusCache,
		on: ( event, callback ) => {
			const set = listeners.get( event ) || new Set();
			set.add( callback );
			listeners.set( event, set );
			// 'ready' is emitted once. A theme module that loads late would
			// otherwise wait forever for an event that has already passed, so
			// replay it -- as a resolved promise still calls .then().
			if ( event === 'ready' && isChatReady ) {
				callback();
			}
			// Waiting for 'ready' means waiting for the chat app, which only
			// exists once the iframe does -- a launcher that stays disabled
			// until then would otherwise wait for a click it cannot receive.
			if ( event === 'ready' && ! isChatReady ) {
				ensureIframe();
			}
		},
		off: ( event, callback ) => {
			const set = listeners.get( event );
			if ( ! set ) {
				return;
			}
			set.delete( callback );
		},
		preload: ensureIframe,
		help: printHelp,
	};
}

// ---------------------------------------------------------------------------
// Delegated entry points
// ---------------------------------------------------------------------------

const TRIGGER_SELECTOR = '[data-dhsv-chat], a[href^="#dhsv-chat"]';
// Prompt triggers in here only open the chat (abuse protection, see the spec).
const COMMENT_AREA_SELECTOR =
	'#comments, .comments-area, .comment-content, .comment-body, .wp-block-comments, .wp-block-comment-content';
const FORM_SELECTOR = 'form[data-dhsv-chat-form]';
const FORM_INPUT_SELECTOR = '[data-dhsv-chat-input]';
const FORM_HINT_CLASS = 'dhsv-chat-input__hint';
const UNREACHABLE_HINT =
	'Der Chat ist gerade nicht erreichbar. Bitte versuchen Sie es erneut.';
// Elements the browser itself turns Enter and Space into a click for.
const NATIVE_KEY_TAGS = new Set( [
	'BUTTON',
	'INPUT',
	'SELECT',
	'TEXTAREA',
	'SUMMARY',
] );

function eventElement( event: Event ): Element | null {
	const target = event.target;
	if ( target instanceof Element ) {
		return target;
	}
	return target instanceof Node ? target.parentElement : null;
}

function isChatTrigger( element: Element ): boolean {
	return (
		element.hasAttribute( 'data-dhsv-chat' ) ||
		( element.tagName === 'A' &&
			parseHashTrigger( element.getAttribute( 'href' ) ) !== null )
	);
}

/**
 * The innermost trigger at or above `start`. `closest()` alone is not enough:
 * `a[href^="#dhsv-chat"]` also matches `#dhsv-chatter`, which is no trigger
 * and must not hide an enclosing one.
 *
 * @param {Element|null} start Where the event came from.
 */
function findTrigger( start: Element | null ): Element | null {
	let candidate = start?.closest( TRIGGER_SELECTOR ) ?? null;
	while ( candidate && ! isChatTrigger( candidate ) ) {
		candidate =
			candidate.parentElement?.closest( TRIGGER_SELECTOR ) ?? null;
	}
	return candidate;
}

function isTriggerDisabled( trigger: Element ): boolean {
	return (
		trigger.hasAttribute( 'disabled' ) ||
		trigger.getAttribute( 'aria-disabled' ) === 'true'
	);
}

/**
 * Entry points open `island` from a desktop sticky bar and `default` everywhere
 * else; an explicit variant wins. A host without a bubble has no bar to match.
 *
 * @param {unknown} value A `data-dhsv-chat-variant` value.
 */
function resolveTriggerVariant( value: unknown ): DrawerVariant {
	return resolveEntryVariant(
		normalizeVariant( value ) as EntryVariantOrUnset,
		{
			launcherMode,
			mobileShell: isMobileShell(),
			forced: hostForced,
		}
	);
}

/**
 * @param {Element} trigger The trigger element.
 * @param {string}  draft   Text to prefill without sending (a key typed on a
 *                          morph trigger); a prompt on the trigger wins.
 */
function activateTrigger( trigger: Element, draft = '' ): void {
	const hash =
		trigger.tagName === 'A'
			? parseHashTrigger( trigger.getAttribute( 'href' ) )
			: null;
	let prompt =
		normalizePrompt( trigger.getAttribute( 'data-dhsv-chat-prompt' ) ) ||
		hash?.prompt ||
		'';
	if ( prompt && trigger.closest( COMMENT_AREA_SELECTOR ) ) {
		prompt = '';
	}
	// A valid data attribute wins over the variant segment of a hash link.
	const attributeVariant = normalizeVariant(
		trigger.getAttribute( 'data-dhsv-chat-variant' )
	);
	const variant =
		attributeVariant !== 'unset'
			? attributeVariant
			: hash?.variant ?? 'unset';
	requestOpen(
		{
			variant: resolveTriggerVariant( variant ),
			explicitVariant: variant !== 'unset',
			hostResolved: true,
			prompt,
			topic: normalizeTopic(
				trigger.getAttribute( 'data-dhsv-chat-topic' )
			),
			source: normalizeSource(
				trigger.getAttribute( 'data-dhsv-chat-source' ),
				'trigger'
			),
			...( draft && ! prompt ? { draft } : {} ),
		},
		trigger
	);
}

function handleTriggerClick( event: MouseEvent ): void {
	if ( event.defaultPrevented || event.button !== 0 ) {
		return;
	}
	const trigger = findTrigger( eventElement( event ) );
	if ( ! trigger || isTriggerDisabled( trigger ) ) {
		return;
	}
	if (
		trigger.tagName === 'A' &&
		( event.ctrlKey || event.metaKey || event.shiftKey || event.altKey )
	) {
		return;
	}
	event.preventDefault();
	activateTrigger( trigger );
}

// Keyboard: the browser covers Enter and Space on native buttons and Enter on
// links. Left to do: Space on `a[role="button"]` (the menu item) and Enter and
// Space on focusable non-native elements.
let armedSpaceTrigger: Element | null = null;

function isRoleButtonLink( element: Element ): boolean {
	return (
		element.tagName === 'A' &&
		element.hasAttribute( 'href' ) &&
		element.getAttribute( 'role' ) === 'button'
	);
}

function isFocusableCustomTrigger( element: Element ): boolean {
	return (
		element.hasAttribute( 'data-dhsv-chat' ) &&
		element.hasAttribute( 'tabindex' ) &&
		! NATIVE_KEY_TAGS.has( element.tagName ) &&
		! ( element.tagName === 'A' && element.hasAttribute( 'href' ) )
	);
}

function keyboardTarget( event: KeyboardEvent ): Element | null {
	if (
		event.isComposing ||
		event.ctrlKey ||
		event.metaKey ||
		event.altKey ||
		event.shiftKey
	) {
		return null;
	}
	const element = eventElement( event );
	if (
		! element ||
		isTriggerDisabled( element ) ||
		! isChatTrigger( element )
	) {
		return null;
	}
	return isRoleButtonLink( element ) || isFocusableCustomTrigger( element )
		? element
		: null;
}

function isSpaceKey( event: KeyboardEvent ): boolean {
	return event.key === ' ' || event.key === 'Spacebar';
}

function handleTriggerKeydown( event: KeyboardEvent ): void {
	const element = keyboardTarget( event );
	if ( ! element ) {
		return;
	}
	if ( isSpaceKey( event ) ) {
		// The page must not scroll (also while the key repeats); the action
		// itself happens once, on keyup.
		event.preventDefault();
		if ( ! event.repeat ) {
			armedSpaceTrigger = element;
		}
		return;
	}
	if (
		event.key === 'Enter' &&
		! event.repeat &&
		isFocusableCustomTrigger( element )
	) {
		event.preventDefault();
		activateTrigger( element );
	}
}

function handleTriggerKeyup( event: KeyboardEvent ): void {
	if ( ! isSpaceKey( event ) ) {
		return;
	}
	const armed = armedSpaceTrigger;
	armedSpaceTrigger = null;
	const element = keyboardTarget( event );
	if ( armed && element === armed ) {
		event.preventDefault();
		activateTrigger( element );
	}
}

/**
 * A morph trigger looks like an input field: any printable key opens the chat
 * and becomes the start of the visitor's question (05-einstiege.md section 2).
 * Enter and Space keep their button behaviour.
 *
 * @param {KeyboardEvent} event A `keydown` event.
 */
function handleMorphTriggerKeydown( event: KeyboardEvent ): void {
	if (
		event.defaultPrevented ||
		event.isComposing ||
		event.repeat ||
		event.ctrlKey ||
		event.metaKey ||
		event.altKey
	) {
		return;
	}
	const key = event.key;
	if ( typeof key !== 'string' || key === ' ' || [ ...key ].length !== 1 ) {
		return;
	}
	const element = eventElement( event );
	if (
		! element ||
		! isMorphTrigger( element ) ||
		! isChatTrigger( element ) ||
		isTriggerDisabled( element )
	) {
		return;
	}
	event.preventDefault();
	activateTrigger( element, key );
}

/**
 * Anything that suggests the visitor is about to use the chat starts loading
 * it: hovering or focusing a trigger, a block field or the teaser.
 *
 * @param {Event} event A `pointerover`, `focusin` or `touchstart` event.
 */
function handlePreloadSignal( event: Event ): void {
	if ( iframe || ! hostElement ) {
		return;
	}
	const element = eventElement( event );
	if (
		element &&
		( findTrigger( element ) ||
			element.closest(
				`[data-dhsv-chat-form], ${ QUICK_SELECTOR }, .${ TEASER_CLASS }`
			) )
	) {
		ensureIframe();
	}
}

// Block form -----------------------------------------------------------------

const composingInputs: WeakSet< Element > = new WeakSet();

function handleCompositionStart( event: Event ): void {
	const element = eventElement( event );
	if ( element?.matches( FORM_INPUT_SELECTOR ) ) {
		composingInputs.add( element );
	}
}

function handleCompositionEnd( event: Event ): void {
	const element = eventElement( event );
	if ( element?.matches( FORM_INPUT_SELECTOR ) ) {
		// A browser that fires the confirming Enter right after `compositionend`
		// must still find the flag set: clear it on the first task after.
		setTimeout( () => composingInputs.delete( element ), 0 );
	}
}

function getFormHint( form: HTMLFormElement ): HTMLElement {
	const existing = form.parentElement?.querySelector< HTMLElement >(
		`:scope > .${ FORM_HINT_CLASS }`
	);
	if ( existing ) {
		return existing;
	}
	// A region has to exist before its text changes to be announced.
	const hint = document.createElement( 'div' );
	hint.className = FORM_HINT_CLASS;
	hint.setAttribute( 'role', 'status' );
	form.insertAdjacentElement( 'afterend', hint );
	return hint;
}

type FormSubmission = {
	/** The question, normalised; empty only opens. */
	prompt: string;
	/** Where a modal open morphs from (the field, or a Schnellfrage). */
	origin: Element;
	/** Shows the busy state until the command is delivered. */
	busyElement: HTMLElement | null;
	/** Attribution fallback when the form names no source. */
	source: string;
	/** Called once the question is in the chat. */
	onDelivered?: () => void;
};

/**
 * Open the chat from a Chat-Eingabe form with a question: the field's submit
 * and its Schnellfragen take this one path (same variant, topic and expand
 * mode). An inline form expands in the page flow; focus returns to its field.
 *
 * @param {HTMLFormElement} form       The block's form.
 * @param {FormSubmission}  submission What to send and where it came from.
 */
function submitChatForm(
	form: HTMLFormElement,
	submission: FormSubmission
): void {
	const input = form.querySelector< HTMLInputElement >( FORM_INPUT_SELECTOR );
	const hint = getFormHint( form );
	hint.textContent = '';
	const inline = isInlineExpandForm( form );

	requestOpen(
		{
			variant: resolveTriggerVariant(
				form.getAttribute( 'data-dhsv-chat-variant' )
			),
			hostResolved: true,
			prompt: submission.prompt,
			topic: normalizeTopic(
				form.getAttribute( 'data-dhsv-chat-topic' )
			),
			source:
				submission.source === 'block'
					? normalizeSource(
							form.getAttribute( 'data-dhsv-chat-source' ),
							'block'
					  )
					: submission.source,
			...( inline ? { inlineForm: form } : {} ),
		},
		// Inline: the chat grows out of the field and focus returns there.
		inline ? input ?? form : submission.origin,
		{
			busyElement: submission.busyElement,
			onDelivered: submission.onDelivered,
			onDiscarded: () => {
				hint.textContent =
					hint.getAttribute( 'data-dhsv-chat-hint' ) ||
					UNREACHABLE_HINT;
			},
		}
	);
}

function handleFormSubmit( event: Event ): void {
	const form = event.target;
	if (
		! ( form instanceof HTMLFormElement ) ||
		! form.matches( FORM_SELECTOR )
	) {
		return;
	}
	event.preventDefault();

	const input = form.querySelector< HTMLInputElement >( FORM_INPUT_SELECTOR );
	if ( input && composingInputs.has( input ) ) {
		return;
	}
	const raw = input?.value ?? '';
	const prompt = normalizePrompt( raw );

	submitChatForm( form, {
		prompt,
		origin: input ?? form,
		busyElement: form.querySelector< HTMLElement >(
			'button[type="submit"], button:not([type])'
		),
		source: 'block',
		// The question is in the chat now; a field the visitor kept
		// typing in is theirs to keep.
		onDelivered: () => {
			if ( input && prompt && input.value === raw ) {
				input.value = '';
			}
		},
	} );
}

/**
 * A Schnellfrage under a Chat-Eingabe: its text goes out as the visitor's
 * first message, exactly like submitting the field with it. Delegated, so
 * blocks added later work too.
 *
 * @param {MouseEvent} event A `click` event.
 */
function handleQuickQuestionClick( event: MouseEvent ): void {
	if ( event.defaultPrevented || event.button !== 0 ) {
		return;
	}
	const quick = findQuickQuestion( eventElement( event ) );
	if ( ! quick ) {
		return;
	}
	event.preventDefault();
	track( ( activeTracker ) =>
		activeTracker.onProactive( 'quick_question_clicked', {
			dhsvchat_quick_index: quick.index,
		} )
	);
	const prompt = normalizePrompt( quick.text );
	if ( quick.form ) {
		submitChatForm( quick.form, {
			prompt,
			origin: quick.button,
			busyElement: quick.button,
			source: 'pill',
		} );
		return;
	}
	requestOpen(
		{
			variant: resolveTriggerVariant( null ),
			hostResolved: true,
			prompt,
			source: 'pill',
		},
		quick.button
	);
}

function installEntryPointListeners(): void {
	// Capture, so a theme's smooth-scroll handler on `a[href^="#"]` that calls
	// `preventDefault()` does not make the click look handled. Propagation is
	// never stopped: the theme's own handler still runs.
	document.addEventListener( 'click', handleTriggerClick, true );
	document.addEventListener( 'keydown', handleTriggerKeydown, true );
	document.addEventListener( 'keydown', handleMorphTriggerKeydown, true );
	document.addEventListener( 'keyup', handleTriggerKeyup, true );
	document.addEventListener( 'pointerover', handlePreloadSignal );
	document.addEventListener( 'focusin', handlePreloadSignal );
	document.addEventListener( 'touchstart', handlePreloadSignal, {
		passive: true,
	} );
	document.addEventListener( 'submit', handleFormSubmit, true );
	document.addEventListener( 'click', handleQuickQuestionClick, true );
	document.addEventListener(
		'compositionstart',
		handleCompositionStart,
		true
	);
	document.addEventListener( 'compositionend', handleCompositionEnd, true );
}

// ---------------------------------------------------------------------------
// Teaser
// ---------------------------------------------------------------------------

const TEASER_CLASS = 'dhsv100-chat-teaser';
// Not rendered below this: there is no room left of the bubble.
const TEASER_MIN_WIDTH_PX = 360;
const TEASER_MIN_HEIGHT_PX = 500;
const TEASER_MAX_LENGTH = 120;
const TEASER_CLOSE_ICON_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

type TeaserConfig = {
	text: string;
	delayS: number;
	frequencyDays: number;
	// Viewport heights to scroll before the teaser may start; 0 = no gate.
	scrollScreens: number;
	closeLabel: string;
	label: string;
	storage: boolean;
};

let teaserConfig: TeaserConfig | null = null;
let teaserEl: HTMLElement | null = null;
let teaserTimer: ReturnType< typeof setTimeout > | null = null;
let teaserDelayElapsed = false;
let teaserScrollGate: ScrollGate | null = null;
// Dismissed or the chat was opened: never again in this page view.
let teaserFinished = false;
// Once-per-page-view guard for blocked or denied storage.
let teaserShownInMemory = false;
// The previous stamp, to undo a showing that a shrinking viewport aborted.
let teaserStampUndo: { previous: string | null } | null = null;

/**
 * `data-teaser-scroll-screens`: a float in 0-20, anything else is 0 (off).
 * @param raw
 */
function parseScrollScreens( raw: string | null ): number {
	const n = Number.parseFloat( raw ?? '' );
	if ( ! Number.isFinite( n ) || n <= 0 ) {
		return 0;
	}
	return Math.min( 20, n );
}

function readTeaserConfig( container: HTMLElement ): TeaserConfig | null {
	const text = ( container.getAttribute( 'data-teaser-text' ) || '' ).trim();
	if ( ! text ) {
		return null;
	}
	return {
		text: Array.from( text ).slice( 0, TEASER_MAX_LENGTH ).join( '' ),
		delayS: clampInt(
			container.getAttribute( 'data-teaser-delay-s' ),
			0,
			120,
			5
		),
		frequencyDays: clampInt(
			container.getAttribute( 'data-teaser-frequency-days' ),
			0,
			365,
			7
		),
		scrollScreens: parseScrollScreens(
			container.getAttribute( 'data-teaser-scroll-screens' )
		),
		closeLabel:
			(
				container.getAttribute( 'data-teaser-close-label' ) || ''
			).trim() || 'Hinweis schließen',
		label:
			( container.getAttribute( 'data-teaser-label' ) || '' ).trim() ||
			'Hinweis zum Chat',
		storage: container.getAttribute( 'data-teaser-storage' ) !== '0',
	};
}

/** Does the frequency rule use `localStorage` at all? */
function teaserUsesStorage(): boolean {
	return (
		!! teaserConfig &&
		teaserConfig.storage &&
		teaserConfig.frequencyDays > 0
	);
}

function readTeaserStamp(): { ok: boolean; value: string | null } {
	try {
		return {
			ok: true,
			value: window.localStorage.getItem( TEASER_STORAGE_KEY ),
		};
	} catch {
		return { ok: false, value: null };
	}
}

function writeTeaserStamp( value: string ): boolean {
	try {
		window.localStorage.setItem( TEASER_STORAGE_KEY, value );
		return true;
	} catch {
		return false;
	}
}

function teaserFits(): boolean {
	return (
		window.innerWidth >= TEASER_MIN_WIDTH_PX &&
		window.innerHeight >= TEASER_MIN_HEIGHT_PX
	);
}

function canShowTeaser(): boolean {
	if ( ! teaserConfig || teaserFinished ) {
		return false;
	}
	if ( teaserConfig.frequencyDays === 0 ) {
		return true;
	}
	if ( ! teaserConfig.storage ) {
		return ! teaserShownInMemory;
	}
	const stamp = readTeaserStamp();
	if ( ! stamp.ok ) {
		return ! teaserShownInMemory;
	}
	return shouldShowTeaser(
		stamp.value,
		Date.now(),
		teaserConfig.frequencyDays
	);
}

/** The teaser was dismissed or the chat was opened: refresh the stamp. */
function stampTeaser(): void {
	teaserStampUndo = null;
	if ( teaserUsesStorage() ) {
		writeTeaserStamp( String( Date.now() ) );
	}
}

function undoTeaserStamp(): void {
	if ( teaserStampUndo ) {
		const { previous } = teaserStampUndo;
		teaserStampUndo = null;
		try {
			if ( previous === null ) {
				window.localStorage.removeItem( TEASER_STORAGE_KEY );
			} else {
				window.localStorage.setItem( TEASER_STORAGE_KEY, previous );
			}
		} catch {
			// Storage went away in between: nothing to restore.
		}
	}
	teaserShownInMemory = false;
}

function removeTeaserElement( fade: boolean ): void {
	clearTeaserSwap();
	clearTeaserAnchorMove();
	const el = teaserEl;
	teaserEl = null;
	renderedSection = null;
	teaserHovered = false;
	if ( ! el ) {
		return;
	}
	if ( ! fade ) {
		el.remove();
		return;
	}
	el.setAttribute( 'inert', '' );
	el.classList.remove( `${ TEASER_CLASS }--visible` );
	setTimeout( () => el.remove(), MOTION_CLOSE_MS + MOTION_SAFETY_MS );
}

function focusLauncherFromTeaser(): void {
	// The placeholder shows one of its controls, depending on the launcher
	// mode and the sticky bar's state.
	let selector = `.${ PLACEHOLDER_CLASS }__bubble`;
	if ( placeholder?.dataset.mode === 'sticky' ) {
		selector =
			placeholder.dataset.state === 'bar'
				? `.${ PLACEHOLDER_CLASS }__pill input`
				: `.${ PLACEHOLDER_CLASS }__shell-bubble`;
	}
	const bubble = placeholder?.querySelector< HTMLElement >( selector );
	if ( bubble ) {
		bubble.focus( { preventScroll: true } );
		return;
	}
	if ( iframe && isChatReady && currentState === 'closed' ) {
		postToIframe( { type: MESSAGE_PREFIX, action: 'focus-launcher' } );
	}
}

function dismissTeaser(): void {
	const hadFocus = !! teaserEl?.contains(
		document.body.ownerDocument.activeElement
	);
	teaserFinished = true;
	stampTeaser();
	removeTeaserElement( true );
	if ( hadFocus ) {
		focusLauncherFromTeaser();
	}
}

/** The chat opened by any entry point: the teaser is gone for this page view. */
function onTeaserSessionStart(): void {
	if ( ! teaserConfig ) {
		return;
	}
	teaserFinished = true;
	teaserScrollGate?.destroy();
	teaserScrollGate = null;
	if ( teaserTimer ) {
		clearTimeout( teaserTimer );
		teaserTimer = null;
	}
	stampTeaser();
	removeTeaserElement( false );
}

function createTeaser(
	config: TeaserConfig,
	container: HTMLElement
): HTMLElement {
	const sizeKey =
		container.getAttribute( 'data-bubble-size' ) || DEFAULT_BUBBLE_SIZE;
	const bubblePx = getBubbleButtonPx( sizeKey );
	const marginPx = parseFloat( getBubbleSizeCss( sizeKey ).margin ) * 16;

	// A named landmark, not a live region: nothing announces it.
	const root = document.createElement( 'aside' );
	root.className = TEASER_CLASS;
	root.setAttribute( 'aria-label', config.label );
	// CSSOM only: allowed under `style-src 'self'`, unlike a style attribute.
	root.style.setProperty( '--dhsv100-teaser-bubble', `${ bubblePx }px` );
	root.style.setProperty( '--dhsv100-teaser-margin', `${ marginPx }px` );

	const text = document.createElement( 'button' );
	text.type = 'button';
	text.className = `${ TEASER_CLASS }__text`;
	text.addEventListener( 'click', () => {
		const section = renderedSection;
		// Before the open, so the click precedes the `open` it causes.
		if ( section ) {
			track( ( activeTracker ) =>
				activeTracker.onProactive( 'proactive_accepted', {
					dhsvchat_section_id: section.id,
					dhsvchat_choice: 'question',
				} )
			);
		} else {
			track( ( activeTracker ) =>
				activeTracker.onTeaserClick(
					'teaser_open',
					text.textContent ?? ''
				)
			);
		}
		teaserFinished = true;
		stampTeaser();
		// The element goes with the open, so the chat still grows from it.
		requestOpen(
			{
				variant: resolveTriggerVariant( null ),
				hostResolved: true,
				prompt: '',
				source: 'teaser',
			},
			text
		);
	} );

	const close = document.createElement( 'button' );
	close.type = 'button';
	close.className = `${ TEASER_CLASS }__close`;
	close.setAttribute( 'aria-label', config.closeLabel );
	close.innerHTML = TEASER_CLOSE_ICON_SVG;
	close.addEventListener( 'click', () => {
		if ( ! trackProactiveDismissal() ) {
			track( ( activeTracker ) =>
				activeTracker.onTeaserClick( 'teaser_close', config.closeLabel )
			);
		}
		dismissTeaser();
	} );

	root.append( text, close );
	root.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Escape' && ! event.defaultPrevented ) {
			trackProactiveDismissal();
			dismissTeaser();
		}
	} );
	// Tracked by events: `:hover` is not reliable everywhere.
	root.addEventListener( 'pointerenter', () => {
		teaserHovered = true;
	} );
	root.addEventListener( 'pointerleave', () => {
		teaserHovered = false;
	} );
	renderTeaserContent( root, config, activeSection );
	return root;
}

function showTeaser(): void {
	if ( ! teaserConfig || ! hostElement ) {
		return;
	}
	teaserEl = createTeaser( teaserConfig, hostElement );
	// Before the placeholder or iframe: text, close, then the bubble in tab order.
	hostElement.insertBefore( teaserEl, hostElement.firstChild );

	if ( teaserConfig.frequencyDays > 0 ) {
		let stamped = false;
		if ( teaserConfig.storage ) {
			const previous = readTeaserStamp();
			if ( previous.ok && writeTeaserStamp( String( Date.now() ) ) ) {
				teaserStampUndo = { previous: previous.value };
				stamped = true;
			}
		}
		// Blocked or denied storage: once per page view.
		teaserShownInMemory = ! stamped;
	}

	const el = teaserEl;
	applyTeaserAnchor( el, currentTeaserAnchor() );
	lastTeaserSwapAt = Date.now();
	trackProactiveShown();
	// Commit the hidden state, then let the transition run.
	void el.offsetWidth;
	el.classList.add( `${ TEASER_CLASS }--visible` );
}

function tryShowTeaser(): void {
	if (
		! teaserConfig ||
		teaserEl ||
		teaserFinished ||
		! teaserDelayElapsed ||
		currentState !== 'closed' ||
		pendingCommand ||
		! teaserFits() ||
		! canShowTeaser()
	) {
		return;
	}
	showTeaser();
}

function handleTeaserResize(): void {
	positionTeaser();
	if ( teaserEl && ! teaserFits() ) {
		// Aborted showing: it must not count against the frequency.
		removeTeaserElement( false );
		undoTeaserStamp();
		return;
	}
	if ( ! teaserEl ) {
		tryShowTeaser();
	}
}

// Anchor (`teaser-anchor.ts`) -------------------------------------------------

let teaserAnchorTimer: ReturnType< typeof setTimeout > | null = null;

function currentTeaserAnchor(): TeaserAnchor {
	return resolveTeaserAnchor( {
		launcherMode,
		stickyBarActive: isStickyBarMode(),
		launcherState: currentLauncherState === 'bar' ? 'bar' : 'bubble',
	} );
}

function clearTeaserAnchorMove(): void {
	if ( teaserAnchorTimer ) {
		clearTimeout( teaserAnchorTimer );
		teaserAnchorTimer = null;
	}
}

/**
 * The strip the sticky bar lives in: the placeholder until the handoff, the
 * collapsed iframe after it. Null when neither is laid out.
 */
function measureStickyStrip(): DOMRect | null {
	const strip = placeholder ?? iframe;
	if ( ! strip ) {
		return null;
	}
	const rect = strip.getBoundingClientRect();
	return rect.width > 0 && rect.height > 0 ? rect : null;
}

/**
 * Put the teaser on an anchor. Above the bar it also takes the bar's
 * measured geometry as custom properties (CSSOM, like the size properties);
 * without a measurement the stylesheet's fallbacks apply.
 *
 * @param {HTMLElement}  el     The teaser element.
 * @param {TeaserAnchor} anchor The anchor.
 */
function applyTeaserAnchor( el: HTMLElement, anchor: TeaserAnchor ): void {
	el.dataset.anchor = anchor;
	if ( anchor !== 'bar' ) {
		return;
	}
	const strip = measureStickyStrip();
	const geometry = strip
		? barTeaserGeometry( {
				strip,
				viewportHeight:
					document.documentElement.clientHeight || window.innerHeight,
		  } )
		: null;
	if ( ! geometry ) {
		return;
	}
	el.style.setProperty( '--dhsv100-teaser-bar-left', `${ geometry.left }px` );
	el.style.setProperty(
		'--dhsv100-teaser-bar-bottom',
		`${ geometry.bottom }px`
	);
	el.style.setProperty(
		'--dhsv100-teaser-bar-width',
		`${ geometry.maxWidth }px`
	);
}

/**
 * Keep the teaser on its launcher: the bar's size or position changed
 * ('launcher-height', resize, visual viewport), or the bar collapsed into
 * its circle or came back. A change of anchor fades out, moves, and fades
 * back in (the section swap's motion); under reduced motion it just moves.
 * Bubble mode always stays on the 'bubble' anchor.
 */
function positionTeaser(): void {
	const el = teaserEl;
	if ( ! el || launcherMode !== 'sticky_bar' ) {
		return;
	}
	const anchor = currentTeaserAnchor();
	if ( el.dataset.anchor === anchor || teaserAnchorTimer ) {
		if ( ! teaserAnchorTimer ) {
			applyTeaserAnchor( el, anchor );
		}
		return;
	}
	if (
		prefersReducedMotion() ||
		! el.classList.contains( `${ TEASER_CLASS }--visible` )
	) {
		applyTeaserAnchor( el, anchor );
		return;
	}
	el.classList.add( `${ TEASER_CLASS }--swapping` );
	teaserAnchorTimer = setTimeout( () => {
		teaserAnchorTimer = null;
		if ( el !== teaserEl ) {
			return;
		}
		// The latest state, not the one that started the move.
		applyTeaserAnchor( el, currentTeaserAnchor() );
		// A section swap on its way removes the class itself.
		if ( ! teaserSwapFadeTimer ) {
			el.classList.remove( `${ TEASER_CLASS }--swapping` );
		}
	}, MOTION_CLOSE_MS );
}

function setupTeaser( container: HTMLElement ): void {
	// Without a visible launcher there is nothing to sit beside. In sticky-bar
	// mode the teaser sits above the bar (or beside its collapsed circle), see
	// `positionTeaser()`.
	if ( ! isBubbleVisible ) {
		return;
	}
	teaserConfig = readTeaserConfig( container );
	if ( ! teaserConfig ) {
		return;
	}
	const { delayS } = teaserConfig;
	const begin = () => {
		teaserTimer = setTimeout( () => {
			teaserTimer = null;
			teaserDelayElapsed = true;
			tryShowTeaser();
		}, delayS * 1000 );
	};
	const start = () => {
		if ( document.visibilityState === 'hidden' ) {
			// A background tab does not count the delay.
			const onVisible = () => {
				if ( document.visibilityState !== 'hidden' ) {
					document.removeEventListener(
						'visibilitychange',
						onVisible
					);
					begin();
				}
			};
			document.addEventListener( 'visibilitychange', onVisible );
		} else {
			begin();
		}
	};
	// The delay only starts once the scroll depth is reached (0 = at once).
	// A page too short to ever get there never shows the teaser.
	teaserScrollGate = createScrollGate( teaserConfig.scrollScreens, () => {
		teaserScrollGate = null;
		if ( ! teaserFinished ) {
			start();
		}
	} );
	window.addEventListener( 'resize', handleTeaserResize, { passive: true } );
	if ( launcherMode === 'sticky_bar' && window.visualViewport ) {
		// Mobile browser chrome and on-screen keyboards move the bottom edge
		// without always firing a window resize.
		window.visualViewport.addEventListener( 'resize', positionTeaser, {
			passive: true,
		} );
	}
	setupProactiveSections();
}

// ---------------------------------------------------------------------------
// Proactive sections (`proactive-sections.ts`)
//
// While the visitor reads a section that carries `data-dhsv-chat-section` and
// `data-dhsv-chat-question`, the teaser shows that question and its pills
// instead of the default text. Same settings, delay, frequency stamp, storage
// filter and minimum viewport as the teaser: it is the same element.
// ---------------------------------------------------------------------------

const TEASER_PILL_CLASS = `${ TEASER_CLASS }__pill`;

// The section in the reading band, or null.
let activeSection: ProactiveSection | null = null;
// What the teaser element shows right now (null: the default text).
let renderedSection: ProactiveSection | null = null;
// The pointer is over the teaser.
let teaserHovered = false;
let lastTeaserSwapAt: number | null = null;
let teaserSwapTimer: ReturnType< typeof setTimeout > | null = null;
let teaserSwapFadeTimer: ReturnType< typeof setTimeout > | null = null;
let sectionObserver: IntersectionObserver | null = null;
// Observed section wrappers in document order.
let sectionElements: Element[] = [];
const sectionData: WeakMap< Element, ProactiveSection > = new WeakMap();
const sectionsInBand: WeakSet< Element > = new WeakSet();
// `proactive_shown` once per section and page view.
const shownSectionIds: Set< string > = new Set();

function prefersReducedMotion(): boolean {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	);
}

function trackProactiveShown(): void {
	const section = renderedSection;
	if ( ! section || shownSectionIds.has( section.id ) ) {
		return;
	}
	shownSectionIds.add( section.id );
	track( ( activeTracker ) =>
		activeTracker.onProactive( 'proactive_shown', {
			dhsvchat_section_id: section.id,
		} )
	);
}

/** Push `proactive_dismissed` when a section question is showing. */
function trackProactiveDismissal(): boolean {
	const section = renderedSection;
	if ( ! section ) {
		return false;
	}
	track( ( activeTracker ) =>
		activeTracker.onProactive( 'proactive_dismissed', {
			dhsvchat_section_id: section.id,
		} )
	);
	return true;
}

function createTeaserPill(
	section: ProactiveSection,
	pill: string,
	index: number
): HTMLButtonElement {
	const button = document.createElement( 'button' );
	button.type = 'button';
	button.className = TEASER_PILL_CLASS;
	button.textContent = pill;
	// For the staggered pop; CSSOM, like the size properties.
	button.style.setProperty( '--dhsv100-teaser-pill-index', String( index ) );
	button.addEventListener( 'click', () => {
		track( ( activeTracker ) =>
			activeTracker.onProactive( 'proactive_accepted', {
				dhsvchat_section_id: section.id,
				dhsvchat_choice: 'pill',
				dhsvchat_pill_index: index,
			} )
		);
		teaserFinished = true;
		stampTeaser();
		// The pill text goes out as the visitor's message.
		requestOpen(
			{
				variant: resolveTriggerVariant( null ),
				hostResolved: true,
				prompt: pill,
				source: 'pill',
			},
			button
		);
	} );
	return button;
}

/**
 * Put the section question and pills (or the default text) into the teaser.
 *
 * @param {HTMLElement}           root    The teaser element.
 * @param {TeaserConfig}          config  The teaser settings.
 * @param {ProactiveSection|null} section The section to show, or null.
 */
function renderTeaserContent(
	root: HTMLElement,
	config: TeaserConfig,
	section: ProactiveSection | null
): void {
	const text = root.querySelector< HTMLElement >(
		`.${ TEASER_CLASS }__text`
	);
	if ( ! text ) {
		return;
	}
	text.textContent = section ? section.question : config.text;
	root.querySelector( `.${ TEASER_CLASS }__pills` )?.remove();
	root.classList.toggle( `${ TEASER_CLASS }--section`, !! section );
	if ( section && section.pills.length > 0 ) {
		const pills = document.createElement( 'div' );
		pills.className = `${ TEASER_CLASS }__pills`;
		section.pills.forEach( ( pill, index ) =>
			pills.append( createTeaserPill( section, pill, index ) )
		);
		// Question, pills, close, then the bubble in tab order.
		text.after( pills );
	}
	renderedSection = section;
}

function clearTeaserSwap(): void {
	if ( teaserSwapTimer ) {
		clearTimeout( teaserSwapTimer );
		teaserSwapTimer = null;
	}
	if ( teaserSwapFadeTimer ) {
		clearTimeout( teaserSwapFadeTimer );
		teaserSwapFadeTimer = null;
	}
	teaserEl?.classList.remove( `${ TEASER_CLASS }--swapping` );
}

function finishTeaserSwap( el: HTMLElement ): void {
	if ( el !== teaserEl || ! teaserConfig ) {
		return;
	}
	renderTeaserContent( el, teaserConfig, activeSection );
	// An anchor move on its way brings the teaser back in itself.
	if ( ! teaserAnchorTimer ) {
		el.classList.remove( `${ TEASER_CLASS }--swapping` );
	}
	trackProactiveShown();
}

function applyTeaserSwap(): void {
	const el = teaserEl;
	if (
		! el ||
		! teaserConfig ||
		sameSection( renderedSection, activeSection )
	) {
		return;
	}
	// Never pull the content from under the pointer or the keyboard.
	if ( teaserHovered || el.contains( el.ownerDocument.activeElement ) ) {
		teaserSwapTimer = setTimeout( () => {
			teaserSwapTimer = null;
			applyTeaserSwap();
		}, 1000 );
		return;
	}
	lastTeaserSwapAt = Date.now();
	if ( prefersReducedMotion() ) {
		finishTeaserSwap( el );
		return;
	}
	// Out, swap while invisible, back in.
	el.classList.add( `${ TEASER_CLASS }--swapping` );
	teaserSwapFadeTimer = setTimeout( () => {
		teaserSwapFadeTimer = null;
		finishTeaserSwap( el );
	}, MOTION_CLOSE_MS );
}

function scheduleTeaserSwap(): void {
	if ( teaserSwapTimer || teaserSwapFadeTimer ) {
		// A swap is on its way and picks up the latest section.
		return;
	}
	const wait = swapDelay( lastTeaserSwapAt, Date.now() );
	if ( wait === 0 ) {
		applyTeaserSwap();
		return;
	}
	teaserSwapTimer = setTimeout( () => {
		teaserSwapTimer = null;
		applyTeaserSwap();
	}, wait );
}

function updateActiveSection(): void {
	const element = pickActiveSection( sectionElements, ( candidate ) =>
		sectionsInBand.has( candidate )
	);
	const next = element ? sectionData.get( element ) ?? null : null;
	if ( sameSection( next, activeSection ) ) {
		return;
	}
	activeSection = next;
	if ( teaserEl ) {
		scheduleTeaserSwap();
	} else if ( next ) {
		// The first section hit may bring the teaser up (after the delay).
		tryShowTeaser();
	}
}

/** Observe the section wrappers that are not observed yet. */
function scanSections(): void {
	if ( ! sectionObserver ) {
		return;
	}
	const found = Array.from( document.querySelectorAll( SECTION_SELECTOR ) );
	let added = false;
	for ( const element of found ) {
		if ( sectionData.has( element ) ) {
			continue;
		}
		const section = readSection( element );
		if ( section ) {
			sectionData.set( element, section );
			sectionObserver.observe( element );
			added = true;
		}
	}
	if ( added ) {
		// `querySelectorAll` returns document order.
		sectionElements = found.filter( ( element ) =>
			sectionData.has( element )
		);
	}
}

function setupProactiveSections(): void {
	if (
		sectionObserver ||
		typeof window.IntersectionObserver !== 'function'
	) {
		return;
	}
	sectionObserver = new window.IntersectionObserver(
		( entries ) => {
			for ( const entry of entries ) {
				if ( entry.isIntersecting ) {
					sectionsInBand.add( entry.target );
				} else {
					sectionsInBand.delete( entry.target );
				}
			}
			updateActiveSection();
		},
		{ rootMargin: SECTION_ROOT_MARGIN }
	);
	scanSections();
	// Content rendered late (lazy blocks, scripts): one more look each.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', scanSections, {
			once: true,
		} );
	}
	if ( document.readyState !== 'complete' ) {
		window.addEventListener( 'load', scanSections, { once: true } );
	}
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

function init(): void {
	const hostContainer = document.getElementById( HOST_CONTAINER_ID );
	if ( ! hostContainer ) {
		// eslint-disable-next-line no-console
		console.warn(
			'dhsv100-ai-chat-plugin: Host container #' +
				HOST_CONTAINER_ID +
				' not found'
		);
		return;
	}

	isBubbleVisible =
		hostContainer.getAttribute( 'data-show-bubble' ) !== 'false';
	// No bubble: either switched off, or the container exists only because a
	// block asked for it. Both mean there is no launcher to match.
	hostForced = ! isBubbleVisible;

	// The host page never receives the full style config, so the one colour the
	// shell has to match travels on the container.
	chatBackgroundColor =
		hostContainer.getAttribute( 'data-chat-background' ) ||
		DEFAULT_CHAT_BACKGROUND;

	// Applied once, at init: Safari honours `viewport-fit` at first layout and
	// does not reliably re-read it when the meta is edited later. Without it
	// the safe areas keep the theme's colour behind the opaque chat shell.
	ensureViewportFitCover();

	readLauncherConfig( hostContainer );
	readBackdropConfig( hostContainer );

	// Tracking: strictly `1` switches it on, so a cached page without the
	// attribute does not track. The tracker is shared by every copy of this
	// script on the page.
	tracker = createTracker( {
		enabled: hostContainer.getAttribute( 'data-tracking' ) === '1',
		storageAllowed:
			hostContainer.getAttribute( 'data-tracking-storage' ) === '1',
	} );
	// A page restored from the back/forward cache is a new visit: the earlier
	// open is gone, so no close is made up for it.
	window.addEventListener( 'pageshow', ( event ) => {
		if ( event.persisted ) {
			track( ( activeTracker ) => activeTracker.onPageRestored() );
		}
	} );

	// Tag fixed elements on the page (for sidebar mode transitions)
	window.addEventListener( 'load', tagFixedElements );

	// No iframe yet -- see ensureIframe(). The scroll observers run anyway, so
	// the placeholder follows bar/bubble and the iframe starts in step.
	hostElement = hostContainer;
	if ( isBubbleVisible ) {
		currentLauncherState = computeLauncherState( false );
		createPlaceholder( hostContainer );
	}
	setupScrollObserver();
	setupLauncherViewportObserver();

	// Listen for messages from iframe
	window.addEventListener( 'message', handleMessage );
	registerApi();
	installEntryPointListeners();
	setupTeaser( hostContainer );
	setupTypingEntries();

	// Announce the API in the same synchronous turn as the assignment above, so
	// a listener registered earlier and a window.DhsvChat check made later can
	// never both miss it. Placed after the early return, so its absence means
	// the chat is unavailable on this page.
	window.dispatchEvent(
		new CustomEvent( DHSV_CHAT_READY_EVENT, {
			detail: { api: window.DhsvChat as DhsvChatApi },
			bubbles: false,
			cancelable: false,
		} )
	);
}

// Run
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
